/**
 * Elite Batch 7 Part 2 — Workflow Templates 568–597
 * Autonomous inbox management, email personalization, drip campaigns, deliverability
 * monitoring, classification, newsletter automation, onboarding sequences, and more.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 7 PART 2 — TEMPLATES 568–597
// =============================================================================

export const ELITE_BATCH7_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 568. AUTONOMOUS INBOX MANAGER (9 nodes)
  // ===================================================================

  "Autonomous Inbox Manager": {
    summary:
      "AI-powered autonomous inbox manager that classifies inbound emails by intent and priority, routes them to CRM, helpdesk, or billing, drafts acknowledgments, logs operations, sends urgent Slack alerts with daily digests, and queues ambiguous items for human review.",
    nodes: [
      trigger("Inbound Email: New message received", "email", {
        source: "Email Server",
        event: "email.received",
        howItStarts:
          "A new inbound email arrives at a monitored inbox, triggering classification and autonomous routing",
        setup:
          "Configure email forwarding from your monitored inbox (support@, info@, etc.) to this workflow webhook. Ensure email body, subject, sender, and headers are included in the payload.",
        inputFields: [],
      }),

      // 2. Content extraction
      n("2", "runCode", "Extract email content and metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract structured data from inbound email payload
const payload = JSON.parse(vars.webhook_payload || "{}");
const sender = payload.from || payload.sender || payload.From || "";
const subject = payload.subject || payload.Subject || "";
const body = payload.text || payload.body || payload.TextBody || payload.HtmlBody || "";
const receivedAt = payload.date || payload.Date || new Date().toISOString();
const hasAttachments = (payload.attachments || payload.Attachments || []).length > 0;
const replyTo = payload.reply_to || payload.ReplyTo || sender;
const messageId = payload.message_id || payload.MessageID || "";
return {
  sender_email: sender,
  email_subject: subject,
  email_body: body.substring(0, 3000),
  received_at: receivedAt,
  has_attachments: hasAttachments,
  reply_to_address: replyTo,
  message_id: messageId,
  body_length: body.length,
  is_reply: subject.toLowerCase().startsWith("re:") || subject.toLowerCase().startsWith("fw:")
};`,
          outputVariables: [
            { name: "sender_email", type: "string" },
            { name: "email_subject", type: "string" },
            { name: "email_body", type: "string" },
            { name: "received_at", type: "string" },
            { name: "has_attachments", type: "boolean" },
            { name: "reply_to_address", type: "string" },
            { name: "message_id", type: "string" },
            { name: "body_length", type: "number" },
            { name: "is_reply", type: "boolean" },
          ],
        },
      }),

      // 3. AI classification + priority
      aiNode(
        "3",
        "AI classify intent and priority",
        520,
        "You are an autonomous inbox triage agent. Classify inbound emails by intent and urgency. Categories: support (technical issues, bugs), billing (invoices, payments, refunds), sales (inquiries, demos, quotes), crm (contact updates, feedback), spam (promotional, unsolicited). Priority: urgent (SLA breach, outage, legal), high (revenue-impacting, escalation), medium (standard request), low (informational, FYI). Respond JSON: {category: string, priority: string, confidence: number, summary: string, suggested_action: string, requires_human: boolean, department: string}",
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}\\nIs reply: {is_reply}\\nHas attachments: {has_attachments}",
        [
          { field: "category", as: "email_category", type: "string" },
          { field: "priority", as: "email_priority", type: "string" },
          {
            field: "confidence",
            as: "classification_confidence",
            type: "number",
          },
          { field: "summary", as: "email_summary", type: "string" },
          { field: "suggested_action", as: "suggested_action", type: "string" },
          {
            field: "requires_human",
            as: "requires_human_review",
            type: "boolean",
          },
          { field: "department", as: "target_department", type: "string" },
        ],
      ),

      // 4. Routing condition: confidence high enough for auto-routing
      n("4", "condition", "Classification confidence >= 0.8?", 760, {
        conditionData: [
          {
            variable: "classification_confidence",
            value: "0.8",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Route to CRM/helpdesk/billing via appropriate API
      n("5", "hitApi", "Route to department system", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubspot.com/crm/v3/objects/tickets",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_pipeline":"0","hs_pipeline_stage":"1","subject":"{email_subject}","content":"{email_summary}","hs_ticket_priority":"{email_priority}","source_type":"EMAIL","category":"{email_category}","sender":"{sender_email}"}',
            },
          ],
          variables: [
            { name: "id", customName: "ticket_id", type: "string" },
            {
              name: "properties.hs_pipeline_stage",
              customName: "ticket_stage",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI acknowledgment draft
      aiNode(
        "6",
        "AI draft acknowledgment reply",
        1240,
        "You are a professional customer communications agent. Draft a concise, warm acknowledgment email for the sender. Reference their specific issue/request by name. Include the ticket/case ID. Set expectations for response time based on priority (urgent: 1hr, high: 4hr, medium: 24hr, low: 48hr). Respond JSON: {subject: string, body: string, estimated_response_time: string}",
        "Original from: {sender_email}\\nSubject: {email_subject}\\nSummary: {email_summary}\\nCategory: {email_category}\\nPriority: {email_priority}\\nTicket ID: {ticket_id}",
        [
          { field: "subject", as: "ack_subject", type: "string" },
          { field: "body", as: "ack_body", type: "string" },
          {
            field: "estimated_response_time",
            as: "response_eta",
            type: "string",
          },
        ],
      ),

      // 7. Log to Sheets
      sheetsNode(
        "7",
        "Log inbox operation (Sheets)",
        1480,
        "InboxOps",
        '[["{received_at}","{sender_email}","{email_category}","{email_priority}","{classification_confidence}","{ticket_id}","{email_summary}","{suggested_action}"]]',
      ),

      // 8. Urgent alert + daily digest
      slackNode(
        "8",
        "Slack: Urgent inbox alert + digest",
        1720,
        ":rotating_light: Urgent Inbox Item\\nFrom: {sender_email}\\nCategory: {email_category} | Priority: {email_priority}\\nSummary: {email_summary}\\nTicket: {ticket_id}\\nSuggested Action: {suggested_action}\\nETA: {response_eta}",
      ),

      // 9. Human review queue (low confidence path)
      sheetsNode(
        "9",
        "Queue for human review (Sheets)",
        1000,
        "HumanReviewQueue",
        '[["{received_at}","{sender_email}","{email_subject}","{email_category}","{email_priority}","{classification_confidence}","{email_summary}","NEEDS_REVIEW"]]',
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> auto-route
      e("4", "9", "output_2"), // low confidence -> human review
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 569. CLICKUP TASK FROM EMAIL CREATOR (7 nodes)
  // ===================================================================

  "ClickUp Task from Email Creator": {
    summary:
      "Converts forwarded emails into structured ClickUp tasks. AI parses subject, body, and attachments to extract title, due date, and assignee, maps to the correct space/folder via runCode, creates the task in ClickUp, links email attachments, notifies the assignee, and sends a confirmation reply.",
    nodes: [
      trigger("Email Forward: Task creation request", "email", {
        source: "Email Forward",
        event: "email.forwarded",
        howItStarts:
          "A team member forwards an email to a designated address to create a ClickUp task automatically",
        setup:
          "Set up an email forwarding address (e.g., task@yourworkflow.com) that sends inbound emails to this workflow webhook. Include full email body, subject, sender, and any attachments.",
        inputFields: [],
      }),

      // 2. AI parsing (title/due/assignee)
      aiNode(
        "2",
        "AI parse task details from email",
        280,
        "You are a project management assistant. Parse forwarded emails to extract task creation details. Find the task title (use email subject if no explicit title), due date (look for dates, deadlines, or phrases like 'by Friday'), assignee (look for names, @mentions, or 'assign to X'), priority (urgent/high/medium/low based on language), and description (summarize the actionable request). Respond JSON: {title: string, due_date: string, assignee_name: string, assignee_email: string, priority: string, description: string, tags: string[], original_sender: string, has_deadline: boolean}",
        "Forwarded email payload: {webhook_payload}",
        [
          { field: "title", as: "task_title", type: "string" },
          { field: "due_date", as: "task_due_date", type: "string" },
          { field: "assignee_name", as: "assignee_name", type: "string" },
          { field: "assignee_email", as: "assignee_email", type: "string" },
          { field: "priority", as: "task_priority", type: "string" },
          { field: "description", as: "task_description", type: "string" },
          { field: "tags", as: "task_tags", type: "string" },
          { field: "original_sender", as: "original_sender", type: "string" },
          { field: "has_deadline", as: "has_deadline", type: "boolean" },
        ],
      ),

      // 3. Space/folder mapping (runCode)
      n("3", "runCode", "Map to ClickUp space and folder", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map task details to appropriate ClickUp space/folder/list
const priority = (vars.task_priority || "medium").toLowerCase();
const tags = JSON.parse(vars.task_tags || "[]");
const title = (vars.task_title || "").toLowerCase();
// Priority mapping for ClickUp (1=urgent, 2=high, 3=normal, 4=low)
const priorityMap = { urgent: 1, high: 2, medium: 3, normal: 3, low: 4 };
const clickupPriority = priorityMap[priority] || 3;
// Determine list based on content keywords
let listId = "{default_list_id}";
let spaceName = "General";
if (title.includes("bug") || title.includes("fix") || title.includes("error")) {
  listId = "{engineering_list_id}";
  spaceName = "Engineering";
} else if (title.includes("design") || title.includes("mockup") || title.includes("ui")) {
  listId = "{design_list_id}";
  spaceName = "Design";
} else if (title.includes("content") || title.includes("blog") || title.includes("copy")) {
  listId = "{marketing_list_id}";
  spaceName = "Marketing";
}
// Format due date for ClickUp (Unix ms)
let dueTimestamp = "";
if (vars.task_due_date && vars.task_due_date !== "none") {
  const d = new Date(vars.task_due_date);
  if (!isNaN(d.getTime())) dueTimestamp = String(d.getTime());
}
return {
  clickup_list_id: listId,
  clickup_priority: clickupPriority,
  space_name: spaceName,
  due_date_ms: dueTimestamp,
  formatted_tags: tags.join(",")
};`,
          outputVariables: [
            { name: "clickup_list_id", type: "string" },
            { name: "clickup_priority", type: "number" },
            { name: "space_name", type: "string" },
            { name: "due_date_ms", type: "string" },
            { name: "formatted_tags", type: "string" },
          ],
        },
      }),

      // 4. Create ClickUp task
      n("4", "hitApi", "Create ClickUp task", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clickup.com/api/v2/list/{clickup_list_id}/task",
          headers: [
            { key: "Authorization", value: "{clickup_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{task_title}" },
            {
              key: "description",
              value:
                "{task_description}\\n\\n---\\nCreated from email by: {original_sender}\\nAssignee: {assignee_name}",
            },
            { key: "priority", value: "{clickup_priority}" },
            { key: "due_date", value: "{due_date_ms}" },
            { key: "tags", value: "[{formatted_tags}]" },
          ],
          variables: [
            { name: "id", customName: "clickup_task_id", type: "string" },
            { name: "url", customName: "clickup_task_url", type: "string" },
            {
              name: "status.status",
              customName: "task_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Assignee notification email
      emailNode(
        "5",
        "Notify assignee of new task",
        1000,
        "{assignee_email}",
        "New Task Assigned: {task_title}",
        "Hi {assignee_name},\\n\\nA new task has been created from an email and assigned to you:\\n\\nTitle: {task_title}\\nPriority: {task_priority}\\nDue: {task_due_date}\\nSpace: {space_name}\\n\\nDescription:\\n{task_description}\\n\\nView task: {clickup_task_url}\\n\\nOriginal sender: {original_sender}",
      ),

      // 6. Send email attachment to ClickUp task as comment
      n("6", "hitApi", "Attach email content to task", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.clickup.com/api/v2/task/{clickup_task_id}/comment",
          headers: [
            { key: "Authorization", value: "{clickup_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "comment_text",
              value:
                "Original email forwarded by {original_sender}. Full task description and context attached above. Email had attachments: check original thread if needed.",
            },
          ],
          variables: [{ name: "id", customName: "comment_id", type: "string" }],
        },
      }),

      // 7. Confirmation reply
      emailNode(
        "7",
        "Send confirmation reply",
        1480,
        "{original_sender}",
        "Task Created: {task_title} (#{clickup_task_id})",
        "Your email has been converted into a ClickUp task.\\n\\nTask: {task_title}\\nID: {clickup_task_id}\\nAssigned to: {assignee_name}\\nPriority: {task_priority}\\nDue: {task_due_date}\\nSpace: {space_name}\\n\\nTrack progress: {clickup_task_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 570. COLD EMAIL PERSONALIZATION AGENT (9 nodes)
  // ===================================================================

  "Cold Email Personalization Agent": {
    summary:
      "AI-powered cold email personalization engine that enriches prospects via Clearbit, scores ICP fit, generates hyper-personalized opening lines and subject lines, runs compliance and spam checks, sends via domain-warming SendGrid, updates HubSpot CRM, schedules follow-ups, and logs all outreach to Sheets.",
    nodes: [
      trigger("Webhook: Prospect ready for outreach", "webhook_app", {
        source: "CRM / Outreach Tool",
        event: "prospect.ready",
        howItStarts:
          "A prospect is marked ready for cold outreach in your CRM or outreach platform, triggering personalized email generation",
        setup:
          "Configure your CRM (HubSpot, Salesforce) or outreach tool to fire a webhook when a prospect enters the outreach-ready stage. Include prospect email, name, company domain, and any known context.",
        inputFields: [],
      }),

      // 2. Clearbit enrichment
      n("2", "hitApi", "Enrich prospect via Clearbit", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://person-stream.clearbit.com/v2/combined/find?email={prospect_email}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "person.name.fullName",
              customName: "prospect_full_name",
              type: "string",
            },
            {
              name: "person.employment.title",
              customName: "prospect_title",
              type: "string",
            },
            {
              name: "person.employment.role",
              customName: "prospect_role",
              type: "string",
            },
            {
              name: "company.name",
              customName: "company_name",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "number",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.annualRevenue",
              customName: "annual_revenue",
              type: "number",
            },
            { name: "company.tech", customName: "tech_stack", type: "string" },
            { name: "company.url", customName: "company_url", type: "string" },
            {
              name: "person.linkedin.handle",
              customName: "linkedin_handle",
              type: "string",
            },
          ],
        },
      }),

      // 3. ICP scoring condition
      n("3", "runCode", "Score ICP fit", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract prospect data from webhook and score ICP fit
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.prospect_email || "";
const companySize = parseInt(vars.company_size) || 0;
const revenue = parseInt(vars.annual_revenue) || 0;
const title = (vars.prospect_title || "").toLowerCase();
const industry = (vars.company_industry || "").toLowerCase();
let score = 0;
// Company size scoring
if (companySize >= 50 && companySize <= 1000) score += 30;
else if (companySize > 1000) score += 20;
else score += 5;
// Revenue scoring
if (revenue >= 5000000) score += 25;
else if (revenue >= 1000000) score += 15;
else score += 5;
// Title scoring (decision makers score higher)
if (title.includes("ceo") || title.includes("cto") || title.includes("vp") || title.includes("director")) score += 25;
else if (title.includes("manager") || title.includes("head")) score += 15;
else score += 5;
// Industry scoring
const targetIndustries = ["technology", "saas", "software", "finance", "healthcare"];
if (targetIndustries.some(i => industry.includes(i))) score += 20;
else score += 10;
const tier = score >= 75 ? "A" : score >= 50 ? "B" : "C";
return {
  prospect_email: email,
  icp_score: score,
  icp_tier: tier,
  score_breakdown: "Size:" + companySize + " Rev:" + revenue + " Title:" + vars.prospect_title + " Industry:" + vars.company_industry,
  is_high_value: score >= 60
};`,
          outputVariables: [
            { name: "prospect_email", type: "string" },
            { name: "icp_score", type: "number" },
            { name: "icp_tier", type: "string" },
            { name: "score_breakdown", type: "string" },
            { name: "is_high_value", type: "boolean" },
          ],
        },
      }),

      // 4. ICP tier condition
      n("4", "condition", "ICP score >= 50 (worth outreach)?", 760, {
        conditionData: [
          {
            variable: "icp_score",
            value: "50",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI personalized opening + subject lines
      aiNode(
        "5",
        "AI personalize email opening + subjects",
        1000,
        "You are an elite cold email copywriter. Using prospect and company data, generate a hyper-personalized cold email. Write 3 subject line variants and a personalized opening paragraph that references something specific about the prospect (their role, company tech stack, industry challenges, or recent company milestones). Keep it conversational, not salesy. The email should feel like it was written by a human who did their research. Respond JSON: {subject_line_1: string, subject_line_2: string, subject_line_3: string, personalized_opening: string, full_email_body: string, personalization_hook: string, compliance_notes: string}",
        "Prospect: {prospect_full_name}, {prospect_title} at {company_name}\\nIndustry: {company_industry}\\nCompany size: {company_size}\\nRevenue: {annual_revenue}\\nTech stack: {tech_stack}\\nLinkedIn: {linkedin_handle}\\nICP tier: {icp_tier}, Score: {icp_score}",
        [
          {
            field: "subject_line_1",
            as: "subject_line_primary",
            type: "string",
          },
          { field: "subject_line_2", as: "subject_line_alt1", type: "string" },
          { field: "subject_line_3", as: "subject_line_alt2", type: "string" },
          {
            field: "personalized_opening",
            as: "personalized_opening",
            type: "string",
          },
          { field: "full_email_body", as: "email_body", type: "string" },
          {
            field: "personalization_hook",
            as: "personalization_hook",
            type: "string",
          },
          { field: "compliance_notes", as: "compliance_notes", type: "string" },
        ],
      ),

      // 6. Domain-warming send (SendGrid)
      n("6", "hitApi", "Send via SendGrid (domain warming)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{prospect_email}","name":"{prospect_full_name}"}],"subject":"{subject_line_primary}"}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{sender_name}"}',
            },
            {
              key: "content",
              value: '[{"type":"text/plain","value":"{email_body}"}]',
            },
            {
              key: "tracking_settings",
              value:
                '{"click_tracking":{"enable":true},"open_tracking":{"enable":true}}',
            },
            {
              key: "custom_args",
              value: '{"icp_tier":"{icp_tier}","campaign":"cold_outreach"}',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 7. HubSpot CRM update
      n("7", "hitApi", "Update HubSpot contact", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/{prospect_email}?idProperty=email",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"cold_outreach_sent":"true","icp_score":"{icp_score}","icp_tier":"{icp_tier}","last_outreach_date":"{now}","outreach_subject":"{subject_line_primary}","personalization_hook":"{personalization_hook}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 8. Follow-up scheduling delay
      n("8", "delayAction", "Schedule follow-up in 3 days", 1720, {
        delayDetails: { delayType: "fixed", delayValue: 3, delayUnit: "days" },
      }),

      // 9. Outreach logging (Sheets)
      sheetsNode(
        "9",
        "Log outreach to Sheets",
        1960,
        "ColdOutreach",
        '[["{prospect_email}","{prospect_full_name}","{company_name}","{icp_tier}","{icp_score}","{subject_line_primary}","{personalization_hook}","{send_status}","{now}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // ICP >= 50 -> personalize
      e("4", "9", "output_2"), // low ICP -> log only
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 571. COLD OUTREACH EMAIL SEQUENCE (8 nodes)
  // ===================================================================

  "Cold Outreach Email Sequence": {
    summary:
      "Multi-touch cold outreach drip sequence that enrolls prospects, enriches their profile, sends a personalized intro email, tracks engagement with delays, branches on open/click signals, sends case study and social proof follow-ups, and ends with a breakup email and stop logic.",
    nodes: [
      trigger("Webhook: Prospect enrolled in outreach", "webhook_app", {
        source: "CRM / Sales Tool",
        event: "prospect.enrolled",
        howItStarts:
          "A prospect is enrolled in the cold outreach sequence from CRM or sales engagement tool",
        setup:
          "Configure CRM or sales tool to fire a webhook when a prospect is enrolled in the outreach sequence. Include prospect email, name, company, and any deal context.",
        inputFields: [],
      }),

      // 2. Prospect enrichment
      aiNode(
        "2",
        "AI extract and enrich prospect data",
        280,
        "You are a data extraction assistant. Parse the webhook payload to find prospect details regardless of field naming. Extract email, first name, last name, company, title, industry, and any context about why they are being contacted. Respond JSON: {email: string, first_name: string, last_name: string, company: string, title: string, industry: string, context: string, pain_points: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "email", as: "prospect_email", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "company", as: "company_name", type: "string" },
          { field: "title", as: "prospect_title", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "context", as: "outreach_context", type: "string" },
          { field: "pain_points", as: "pain_points", type: "string" },
        ],
      ),

      // 3. Day-1 intro email
      emailNode(
        "3",
        "Day 1: Personalized intro email",
        520,
        "{prospect_email}",
        "Quick question about {company_name}",
        "Hi {first_name},\\n\\nI noticed {company_name} is in the {industry} space and wanted to reach out. {outreach_context}\\n\\nWe have been helping similar companies tackle {pain_points} and I thought it might be worth a quick conversation.\\n\\nWould you be open to a 15-minute chat this week?\\n\\nBest,\\n{sender_name}",
      ),

      // 4. Open/click tracking delay
      n("4", "delayAction", "Wait 3 days for engagement signals", 760, {
        delayDetails: { delayType: "fixed", delayValue: 3, delayUnit: "days" },
      }),

      // 5. Engagement condition
      n("5", "condition", "Email opened or clicked?", 1000, {
        conditionData: [
          {
            variable: "email_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "email_clicked",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Case study follow-up (engaged path)
      emailNode(
        "6",
        "Day 4: Case study follow-up",
        1240,
        "{prospect_email}",
        "How {industry} companies solve {pain_points}",
        "Hi {first_name},\\n\\nFollowing up on my previous note. I wanted to share a quick case study of how a {industry} company similar to {company_name} achieved significant results.\\n\\nThey were dealing with {pain_points} and within 90 days saw measurable improvements across their key metrics.\\n\\nHappy to walk you through the details. Would any day this week work for a brief call?\\n\\nBest,\\n{sender_name}",
      ),

      // 7. Social proof follow-up (non-engaged path)
      emailNode(
        "7",
        "Day 4: Social proof nudge",
        1240,
        "{prospect_email}",
        "{first_name}, thought you might find this interesting",
        "Hi {first_name},\\n\\nI know inboxes are busy so I will keep this brief.\\n\\nOur clients in {industry} typically see results within the first quarter. Companies like yours with similar challenges around {pain_points} have found our approach particularly effective.\\n\\nIf the timing is not right, no worries at all. But if you are curious, I am happy to share more.\\n\\nCheers,\\n{sender_name}",
        500,
      ),

      // 8. Breakup email + stop logic
      emailNode(
        "8",
        "Day 10: Breakup email",
        1480,
        "{prospect_email}",
        "Closing the loop, {first_name}",
        "Hi {first_name},\\n\\nI have reached out a couple of times and understand you might be swamped or this might not be a priority right now. No hard feelings at all.\\n\\nI will close out my follow-ups, but if {pain_points} ever becomes a priority for {company_name}, feel free to reply to this thread anytime.\\n\\nWishing you all the best,\\n{sender_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // engaged -> case study
      e("5", "7", "output_2"), // not engaged -> social proof
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 572. DRIP CAMPAIGN ENGAGEMENT SCORER (8 nodes)
  // ===================================================================

  "Drip Campaign Engagement Scorer": {
    summary:
      "Calculates weighted engagement scores for drip campaign recipients using open, click, reply, and conversion events. Updates cumulative scores in HubSpot, routes hot leads to sales via Slack, adjusts content frequency, suppresses cold contacts, and logs scoring data to a dashboard sheet.",
    nodes: [
      trigger("Webhook: Campaign engagement event", "webhook_app", {
        source: "Email Service Provider",
        event: "campaign.event",
        howItStarts:
          "An engagement event (open, click, reply, unsubscribe) fires from the email service provider for a drip campaign recipient",
        setup:
          "Configure SendGrid/Mailchimp/Postmark event webhooks to fire on open, click, reply, bounce, and unsubscribe events. Include recipient email, event type, campaign ID, and timestamp.",
        inputFields: [],
      }),

      // 2. Weighted score calculation (runCode)
      n("2", "runCode", "Calculate weighted engagement score", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse event and calculate weighted engagement score
const payload = JSON.parse(vars.webhook_payload || "{}");
const eventType = payload.event || payload.type || payload.RecordType || "";
const email = payload.email || payload.recipient || payload.Recipient || "";
const campaignId = payload.campaign_id || payload.sg_message_id || "";
const timestamp = payload.timestamp || payload.date || new Date().toISOString();
// Weighted scoring by event type
const weights = {
  open: 5, click: 15, reply: 30, conversion: 50,
  unsubscribe: -50, bounce: -30, spam_report: -100
};
const eventKey = eventType.toLowerCase().replace(/[^a-z_]/g, "");
const eventScore = weights[eventKey] || 0;
// Recency multiplier (events within 7 days get 1.5x, within 30 days get 1x, older get 0.5x)
const eventDate = new Date(timestamp);
const daysSince = Math.floor((Date.now() - eventDate.getTime()) / 86400000);
const recencyMultiplier = daysSince <= 7 ? 1.5 : daysSince <= 30 ? 1.0 : 0.5;
const weightedScore = Math.round(eventScore * recencyMultiplier);
return {
  contact_email: email,
  event_type: eventType,
  event_score: eventScore,
  weighted_score: weightedScore,
  recency_days: daysSince,
  recency_multiplier: recencyMultiplier,
  campaign_id: campaignId,
  event_timestamp: timestamp,
  is_negative_event: eventScore < 0
};`,
          outputVariables: [
            { name: "contact_email", type: "string" },
            { name: "event_type", type: "string" },
            { name: "event_score", type: "number" },
            { name: "weighted_score", type: "number" },
            { name: "recency_days", type: "number" },
            { name: "recency_multiplier", type: "number" },
            { name: "campaign_id", type: "string" },
            { name: "event_timestamp", type: "string" },
            { name: "is_negative_event", type: "boolean" },
          ],
        },
      }),

      // 3. HubSpot cumulative score update
      n("3", "hitApi", "Update cumulative score in HubSpot", 520, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/{contact_email}?idProperty=email",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"engagement_score_delta":"{weighted_score}","last_engagement_event":"{event_type}","last_engagement_date":"{event_timestamp}","engagement_campaign":"{campaign_id}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
            {
              name: "properties.engagement_score",
              customName: "cumulative_score",
              type: "number",
            },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
          ],
        },
      }),

      // 4. Threshold condition
      n("4", "condition", "Cumulative score >= 100 (hot lead)?", 760, {
        conditionData: [
          {
            variable: "cumulative_score",
            value: "100",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Hot lead sales handoff (Slack)
      slackNode(
        "5",
        "Slack: Hot lead sales alert",
        1000,
        ":fire: Hot Lead Alert — Engagement Score Threshold Reached\\nContact: {contact_email}\\nCumulative Score: {cumulative_score}\\nLatest Event: {event_type} (+{weighted_score} pts)\\nCampaign: {campaign_id}\\nLifecycle: {lifecycle_stage}\\nAction: Assign to sales rep for immediate follow-up",
      ),

      // 6. Content frequency adjustment
      n("6", "runCode", "Adjust content frequency", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine content frequency adjustment based on score
const score = parseInt(vars.cumulative_score) || 0;
const isNegative = vars.is_negative_event === "true";
let frequency = "normal";
let action = "continue";
if (score >= 100) {
  frequency = "pause_drip";
  action = "handoff_to_sales";
} else if (score >= 60) {
  frequency = "accelerate";
  action = "increase_touchpoints";
} else if (score <= -20 || isNegative) {
  frequency = "reduce";
  action = "decrease_frequency";
} else if (score <= 0) {
  frequency = "suppress_candidate";
  action = "evaluate_for_suppression";
}
return {
  frequency_adjustment: frequency,
  recommended_action: action,
  should_suppress: score <= -30
};`,
          outputVariables: [
            { name: "frequency_adjustment", type: "string" },
            { name: "recommended_action", type: "string" },
            { name: "should_suppress", type: "boolean" },
          ],
        },
      }),

      // 7. Cold suppression (low score path)
      n(
        "7",
        "hitApi",
        "Suppress cold contact in HubSpot",
        1000,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubspot.com/crm/v3/objects/contacts/{contact_email}?idProperty=email",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"drip_status":"suppressed","suppression_reason":"low_engagement_score","frequency_adjustment":"{frequency_adjustment}"}',
              },
            ],
            variables: [
              {
                name: "id",
                customName: "suppressed_contact_id",
                type: "string",
              },
            ],
          },
        },
        500,
      ),

      // 8. Score dashboard (Sheets)
      sheetsNode(
        "8",
        "Log engagement score (Sheets)",
        1480,
        "EngagementScores",
        '[["{event_timestamp}","{contact_email}","{event_type}","{event_score}","{weighted_score}","{cumulative_score}","{frequency_adjustment}","{recommended_action}","{campaign_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // hot lead -> sales alert
      e("4", "6", "output_2"), // not hot -> frequency adjustment
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 573. EMAIL CLASSIFICATION AND ROUTING AGENT (8 nodes)
  // ===================================================================

  "Email Classification and Routing Agent": {
    summary:
      "Intelligent email classification agent that extracts content from inbound emails, uses AI to classify intent and urgency with confidence scoring, creates tickets in Zendesk or records in HubSpot based on classification, sends acknowledgment emails, logs classifications to Sheets, and routes low-confidence items for human review.",
    nodes: [
      trigger("Inbound Email: New message for classification", "email", {
        source: "Email Server",
        event: "email.received",
        howItStarts:
          "A new email arrives at a monitored inbox (support@, hello@, etc.) and needs to be classified and routed to the correct team",
        setup:
          "Configure email forwarding from monitored inboxes to this workflow webhook. Ensure full email payload including headers, subject, body, sender, and attachments metadata is forwarded.",
        inputFields: [],
      }),

      // 2. Content extraction
      n("2", "runCode", "Extract and normalize email content", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and normalize email fields from various formats
const payload = JSON.parse(vars.webhook_payload || "{}");
const sender = payload.from || payload.From || payload.sender || payload.envelope?.from || "";
const subject = payload.subject || payload.Subject || "";
const textBody = payload.text || payload.TextBody || payload.body || "";
const htmlBody = payload.html || payload.HtmlBody || "";
const body = textBody || htmlBody.replace(/<[^>]*>/g, " ").substring(0, 3000);
const cc = payload.cc || payload.Cc || "";
const attachmentCount = (payload.attachments || payload.Attachments || []).length;
const isAutoReply = (payload.headers || []).some(h => h.Name === "Auto-Submitted" && h.Value !== "no");
return {
  sender_email: sender,
  email_subject: subject,
  email_body: body.substring(0, 3000),
  cc_recipients: cc,
  attachment_count: attachmentCount,
  is_auto_reply: isAutoReply,
  word_count: body.split(/\\s+/).length,
  received_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "sender_email", type: "string" },
            { name: "email_subject", type: "string" },
            { name: "email_body", type: "string" },
            { name: "cc_recipients", type: "string" },
            { name: "attachment_count", type: "number" },
            { name: "is_auto_reply", type: "boolean" },
            { name: "word_count", type: "number" },
            { name: "received_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI classification + urgency
      aiNode(
        "3",
        "AI classify email intent and urgency",
        520,
        "You are an email classification agent. Analyze the email and classify it into exactly one category: support_technical (bugs, errors, outages), support_billing (invoices, charges, refunds), sales_inquiry (pricing, demos, capabilities), feedback (suggestions, complaints, praise), partnership (integration, reseller, collaboration), spam (promotional, phishing, irrelevant), or other. Determine urgency: critical (system down, data loss, legal), high (revenue impact, deadline), medium (standard request), low (informational). Assess your confidence 0-1. Respond JSON: {category: string, urgency: string, confidence: number, summary: string, key_entities: string[], suggested_team: string, auto_response_appropriate: boolean}",
        "From: {sender_email}\\nSubject: {email_subject}\\nBody: {email_body}\\nAttachments: {attachment_count}\\nIs auto-reply: {is_auto_reply}",
        [
          { field: "category", as: "email_category", type: "string" },
          { field: "urgency", as: "email_urgency", type: "string" },
          { field: "confidence", as: "confidence_score", type: "number" },
          { field: "summary", as: "email_summary", type: "string" },
          { field: "key_entities", as: "key_entities", type: "string" },
          { field: "suggested_team", as: "suggested_team", type: "string" },
          {
            field: "auto_response_appropriate",
            as: "can_auto_respond",
            type: "boolean",
          },
        ],
      ),

      // 4. Confidence condition
      n("4", "condition", "Classification confidence >= 0.75?", 760, {
        conditionData: [
          {
            variable: "confidence_score",
            value: "0.75",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Ticket/record creation (Zendesk)
      n("5", "hitApi", "Create Zendesk ticket", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"subject":"{email_subject}","description":"{email_summary}","requester":{"email":"{sender_email}"},"priority":"{email_urgency}","tags":["{email_category}","{suggested_team}"],"group_id":"{routing_group_id}"}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "zendesk_ticket_id",
              type: "number",
            },
            {
              name: "ticket.url",
              customName: "zendesk_ticket_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Acknowledgment email
      emailNode(
        "6",
        "Send classification acknowledgment",
        1240,
        "{sender_email}",
        "Re: {email_subject} [Ticket #{zendesk_ticket_id}]",
        "Hi,\\n\\nThank you for reaching out. We have received your message and created ticket #{zendesk_ticket_id}.\\n\\nCategory: {email_category}\\nPriority: {email_urgency}\\nAssigned Team: {suggested_team}\\n\\nOur {suggested_team} team will respond within the standard SLA for {email_urgency} priority requests.\\n\\nBest regards,\\nSupport Team",
      ),

      // 7. Classification logging (Sheets)
      sheetsNode(
        "7",
        "Log email classification (Sheets)",
        1480,
        "EmailClassification",
        '[["{received_timestamp}","{sender_email}","{email_category}","{email_urgency}","{confidence_score}","{suggested_team}","{zendesk_ticket_id}","{email_summary}"]]',
      ),

      // 8. Low-confidence human review
      slackNode(
        "8",
        "Slack: Low-confidence review request",
        1000,
        ":question: Low-Confidence Email Classification — Human Review Needed\\nFrom: {sender_email}\\nSubject: {email_subject}\\nAI Category: {email_category} (confidence: {confidence_score})\\nUrgency: {email_urgency}\\nSummary: {email_summary}\\nPlease review and manually route this email.",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> auto-route
      e("4", "8", "output_2"), // low confidence -> human review
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 574. EMAIL DELIVERABILITY MONITOR (8 nodes)
  // ===================================================================

  "Email Deliverability Monitor": {
    summary:
      "Hourly monitoring of email deliverability metrics from SendGrid and Postmark. Compares current bounce, complaint, and delivery rates against historical baselines, alerts on severity thresholds, auto-suppresses hard bounces, updates dashboards, and logs incidents for post-mortem analysis.",
    nodes: [
      trigger("Schedule: Hourly deliverability check", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to pull email deliverability statistics from SendGrid and Postmark, comparing against baseline thresholds",
        setup:
          "Configure SendGrid and Postmark API keys. Set baseline thresholds for bounce rate (<2%), complaint rate (<0.1%), and delivery rate (>98%). Set up Slack webhook for critical alerts.",
        inputFields: [],
      }),

      // 2. Pull stats from SendGrid + Postmark
      n("2", "hitApi", "Pull SendGrid deliverability stats", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.sendgrid.com/v3/stats?start_date={today_date}&aggregated_by=day",
          headers: [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
          body: [],
          variables: [
            {
              name: "[0].stats[0].metrics.delivered",
              customName: "sg_delivered",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.bounces",
              customName: "sg_bounces",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.spam_reports",
              customName: "sg_spam_reports",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.requests",
              customName: "sg_requests",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.opens",
              customName: "sg_opens",
              type: "number",
            },
            {
              name: "[0].stats[0].metrics.clicks",
              customName: "sg_clicks",
              type: "number",
            },
          ],
        },
      }),

      // 3. Baseline comparison (runCode)
      n("3", "runCode", "Compare against deliverability baselines", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate deliverability KPIs and compare to baselines
const requests = parseInt(vars.sg_requests) || 1;
const delivered = parseInt(vars.sg_delivered) || 0;
const bounces = parseInt(vars.sg_bounces) || 0;
const spamReports = parseInt(vars.sg_spam_reports) || 0;
const opens = parseInt(vars.sg_opens) || 0;
const clicks = parseInt(vars.sg_clicks) || 0;
const deliveryRate = ((delivered / requests) * 100).toFixed(2);
const bounceRate = ((bounces / requests) * 100).toFixed(2);
const complaintRate = ((spamReports / requests) * 100).toFixed(3);
const openRate = ((opens / delivered) * 100).toFixed(2);
const clickRate = ((clicks / delivered) * 100).toFixed(2);
// Baseline thresholds
const BOUNCE_THRESHOLD = 2.0;
const COMPLAINT_THRESHOLD = 0.1;
const DELIVERY_THRESHOLD = 98.0;
const bounceExceeded = parseFloat(bounceRate) > BOUNCE_THRESHOLD;
const complaintExceeded = parseFloat(complaintRate) > COMPLAINT_THRESHOLD;
const deliveryBelow = parseFloat(deliveryRate) < DELIVERY_THRESHOLD;
let severity = "normal";
if (complaintExceeded || parseFloat(bounceRate) > 5) severity = "critical";
else if (bounceExceeded || deliveryBelow) severity = "warning";
return {
  delivery_rate: deliveryRate,
  bounce_rate: bounceRate,
  complaint_rate: complaintRate,
  open_rate: openRate,
  click_rate: clickRate,
  total_sent: requests,
  total_delivered: delivered,
  total_bounced: bounces,
  severity_level: severity,
  bounce_exceeded: bounceExceeded,
  complaint_exceeded: complaintExceeded,
  needs_action: severity !== "normal",
  check_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "delivery_rate", type: "string" },
            { name: "bounce_rate", type: "string" },
            { name: "complaint_rate", type: "string" },
            { name: "open_rate", type: "string" },
            { name: "click_rate", type: "string" },
            { name: "total_sent", type: "number" },
            { name: "total_delivered", type: "number" },
            { name: "total_bounced", type: "number" },
            { name: "severity_level", type: "string" },
            { name: "bounce_exceeded", type: "boolean" },
            { name: "complaint_exceeded", type: "boolean" },
            { name: "needs_action", type: "boolean" },
            { name: "check_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Severity condition
      n("4", "condition", "Severity is critical or warning?", 760, {
        conditionData: [
          {
            variable: "needs_action",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Critical Slack alert
      slackNode(
        "5",
        "Slack: Deliverability alert",
        1000,
        ":warning: Email Deliverability Alert — Severity: {severity_level}\\nDelivery Rate: {delivery_rate}% | Bounce Rate: {bounce_rate}% | Complaint Rate: {complaint_rate}%\\nSent: {total_sent} | Delivered: {total_delivered} | Bounced: {total_bounced}\\nOpen Rate: {open_rate}% | Click Rate: {click_rate}%\\nAction Required: Review sending patterns and check for list quality issues.",
      ),

      // 6. Auto hard-bounce suppression
      n("6", "hitApi", "Suppress hard bounces (SendGrid)", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.sendgrid.com/v3/suppression/bounces?start_time={hour_ago_epoch}",
          headers: [{ key: "Authorization", value: "Bearer {sendgrid_key}" }],
          body: [],
          variables: [
            { name: "length", customName: "new_bounces_count", type: "number" },
          ],
        },
      }),

      // 7. Dashboard update (Sheets)
      sheetsNode(
        "7",
        "Update deliverability dashboard (Sheets)",
        1480,
        "Deliverability",
        '[["{check_timestamp}","{delivery_rate}","{bounce_rate}","{complaint_rate}","{open_rate}","{click_rate}","{total_sent}","{total_bounced}","{severity_level}","{new_bounces_count}"]]',
      ),

      // 8. Incident logging
      sheetsNode(
        "8",
        "Log deliverability incident (Sheets)",
        1720,
        "DeliverabilityIncidents",
        '[["{check_timestamp}","{severity_level}","{bounce_rate}","{complaint_rate}","{delivery_rate}","{total_sent}","Bounce exceeded: {bounce_exceeded}, Complaint exceeded: {complaint_exceeded}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // needs action -> alert
      e("4", "7", "output_2"), // normal -> just log
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 575. EMAIL FOLLOW UP REMINDER (7 nodes)
  // ===================================================================

  "Email Follow Up Reminder": {
    summary:
      "Tracks sent emails and automatically generates contextual follow-ups when recipients do not reply within a configured window. Waits for reply detection, branches on response status, drafts AI-powered follow-ups, applies approval logic, sends the follow-up, and logs all attempts to Sheets.",
    nodes: [
      trigger("Webhook: Tracked email sent", "webhook_app", {
        source: "Email Tracking Service",
        event: "email.sent",
        howItStarts:
          "A tracked email is sent and monitoring begins for a reply within the configured time window",
        setup:
          "Configure your email sending service (SendGrid, Gmail API) to fire a webhook when a tracked email is sent. Include recipient, subject, body summary, and message ID for thread matching.",
        inputFields: [],
      }),

      // 2. Reply check delay
      n("2", "delayAction", "Wait 48 hours for reply", 280, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 3. Reply detection condition
      n("3", "condition", "Reply received within window?", 520, {
        conditionData: [
          {
            variable: "reply_received",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI contextual follow-up draft
      aiNode(
        "4",
        "AI draft contextual follow-up",
        760,
        "You are a professional email follow-up assistant. Given the context of the original email that was sent and not replied to, draft a polite, concise follow-up. Reference the original subject matter, add new value or a different angle, and include a clear call to action. Keep it under 150 words. Do not be pushy. Respond JSON: {subject: string, body: string, tone: string, new_angle: string}",
        "Original email context from webhook: {webhook_payload}\\nDays since sent: 2\\nRecipient has not replied.",
        [
          { field: "subject", as: "followup_subject", type: "string" },
          { field: "body", as: "followup_body", type: "string" },
          { field: "tone", as: "followup_tone", type: "string" },
          { field: "new_angle", as: "new_angle", type: "string" },
        ],
      ),

      // 5. Auto-send/approval condition
      n("5", "condition", "Auto-send enabled?", 1000, {
        conditionData: [
          {
            variable: "auto_send_followups",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Send follow-up
      emailNode(
        "6",
        "Send follow-up email",
        1240,
        "{recipient_email}",
        "{followup_subject}",
        "{followup_body}",
      ),

      // 7. Attempt logging (Sheets)
      sheetsNode(
        "7",
        "Log follow-up attempt (Sheets)",
        1480,
        "FollowUpLog",
        '[["{now}","{recipient_email}","{followup_subject}","{followup_tone}","{new_angle}","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_2"), // no reply -> draft follow-up
      e("3", "7", "output_1"), // reply received -> just log
      e("4", "5"),
      e("5", "6", "output_1"), // auto-send -> send
      e("5", "7", "output_2"), // manual approval -> log for review
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 576. EMAIL INBOX SUMMARIZER (7 nodes)
  // ===================================================================

  "Email Inbox Summarizer": {
    summary:
      "Scheduled email inbox summarizer that fetches recent emails within a time window, uses AI to group and summarize them by topic and urgency, extracts action items, delivers a formatted summary email, posts a condensed version to Slack, and archives the summary log.",
    nodes: [
      trigger("Schedule: Daily inbox summary", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to fetch, group, and summarize the previous 24 hours of inbox emails into an actionable digest",
        setup:
          "Configure Gmail API or IMAP credentials for the target inbox. Set the summary delivery email address and Slack webhook. Optionally filter by label or sender domain.",
        inputFields: [],
      }),

      // 2. Email window fetch
      n("2", "hitApi", "Fetch recent emails (Gmail API)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://gmail.googleapis.com/gmail/v1/users/me/messages?q=newer_than:1d&maxResults=100",
          headers: [{ key: "Authorization", value: "Bearer {gmail_token}" }],
          body: [],
          variables: [
            { name: "messages", customName: "message_list", type: "string" },
            {
              name: "resultSizeEstimate",
              customName: "total_messages",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI grouping + summarization
      aiNode(
        "3",
        "AI group and summarize inbox",
        520,
        "You are an executive assistant summarizing an inbox. Group emails by topic/thread, rank by urgency and importance, and provide a concise summary of each group. Identify the top 3 most important items requiring action. Flag anything time-sensitive. Respond JSON: {groups: [{topic: string, count: number, urgency: string, summary: string, sender_highlights: string}], total_groups: number, urgent_count: number, top_actions: string, executive_summary: string}",
        "Email data for summarization: {message_list}\\nTotal messages: {total_messages}",
        [
          { field: "groups", as: "email_groups", type: "string" },
          { field: "total_groups", as: "total_groups", type: "number" },
          { field: "urgent_count", as: "urgent_count", type: "number" },
          { field: "top_actions", as: "top_actions", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 4. Action list extraction (runCode)
      n("4", "runCode", "Extract structured action list", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the AI summary into structured action items
const groups = JSON.parse(vars.email_groups || "[]");
const urgentGroups = groups.filter(g => g.urgency === "high" || g.urgency === "critical");
const actionItems = urgentGroups.map((g, i) => (i + 1) + ". [" + g.urgency.toUpperCase() + "] " + g.topic + ": " + g.summary).join("\\n");
const fullSummary = groups.map((g, i) => "**" + g.topic + "** (" + g.count + " emails, " + g.urgency + ")\\n" + g.summary).join("\\n\\n");
return {
  action_items: actionItems || "No urgent action items today.",
  formatted_summary: fullSummary,
  urgent_group_count: urgentGroups.length,
  summary_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "action_items", type: "string" },
            { name: "formatted_summary", type: "string" },
            { name: "urgent_group_count", type: "number" },
            { name: "summary_date", type: "string" },
          ],
        },
      }),

      // 5. Summary email delivery
      emailNode(
        "5",
        "Send inbox summary email",
        1000,
        "{summary_recipient_email}",
        "Daily Inbox Summary — {summary_date} ({total_messages} emails, {urgent_count} urgent)",
        "Good morning,\\n\\nHere is your inbox summary for {summary_date}:\\n\\n{executive_summary}\\n\\n--- ACTION ITEMS ---\\n{action_items}\\n\\n--- FULL SUMMARY ---\\n{formatted_summary}\\n\\nTotal: {total_messages} emails across {total_groups} topics.",
      ),

      // 6. Slack condensed version
      slackNode(
        "6",
        "Slack: Condensed inbox summary",
        1240,
        ":email: Daily Inbox Summary — {summary_date}\\nEmails: {total_messages} | Groups: {total_groups} | Urgent: {urgent_count}\\n\\nTop Actions:\\n{top_actions}\\n\\nExecutive Summary: {executive_summary}",
      ),

      // 7. Archive logging
      sheetsNode(
        "7",
        "Archive summary log (Sheets)",
        1480,
        "InboxSummaryArchive",
        '[["{summary_date}","{total_messages}","{total_groups}","{urgent_count}","{executive_summary}","{action_items}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 577. EMAIL NURTURE SEQUENCE (8 nodes)
  // ===================================================================

  "Email Nurture Sequence": {
    summary:
      "Multi-stage email nurture sequence for marketing-qualified leads. Sends a welcome email, delays for engagement checks, delivers educational content, evaluates mid-sequence engagement score, branches to offer/demo CTA or objection handling, and hands off engaged leads to sales via Slack.",
    nodes: [
      trigger("Webhook: Lead enters nurture segment", "webhook_app", {
        source: "Marketing Automation / CRM",
        event: "segment.entered",
        howItStarts:
          "A lead enters the nurture segment after meeting qualification criteria (e.g., downloaded content, attended webinar, visited pricing page)",
        setup:
          "Configure your marketing platform or CRM to fire a webhook when a lead enters the nurture segment. Include lead email, name, source, and any behavioral data.",
        inputFields: [],
      }),

      // 2. Welcome email
      aiNode(
        "2",
        "AI extract lead data + draft welcome",
        280,
        "You are a data extraction and email drafting assistant. Parse the webhook payload to find lead email, name, company, and source. Then draft a warm, non-salesy welcome email that acknowledges how they found you and sets expectations for the nurture sequence. Respond JSON: {email: string, first_name: string, company: string, source: string, welcome_subject: string, welcome_body: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "email", as: "lead_email", type: "string" },
          { field: "first_name", as: "lead_first_name", type: "string" },
          { field: "company", as: "lead_company", type: "string" },
          { field: "source", as: "lead_source", type: "string" },
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
        ],
      ),

      // 3. Send welcome email
      emailNode(
        "3",
        "Send welcome email",
        520,
        "{lead_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 4. Delay + engagement check
      n("4", "delayAction", "Wait 4 days for engagement", 760, {
        delayDetails: { delayType: "fixed", delayValue: 4, delayUnit: "days" },
      }),

      // 5. Educational content email
      emailNode(
        "5",
        "Send educational content email",
        1000,
        "{lead_email}",
        "{lead_first_name}, here is something that might help",
        "Hi {lead_first_name},\\n\\nBased on your interest from {lead_source}, I thought you would find this helpful.\\n\\nWe put together a guide on the top challenges facing teams at companies like {lead_company} and practical strategies to overcome them.\\n\\nKey takeaways:\\n1. Common pitfalls and how to avoid them\\n2. Best practices from industry leaders\\n3. Actionable frameworks you can implement this week\\n\\n[Read the full guide]\\n\\nHope this is useful!\\nThe Team",
      ),

      // 6. Mid-sequence score condition
      n("6", "condition", "Lead engagement score >= 50?", 1240, {
        conditionData: [
          {
            variable: "lead_engagement_score",
            value: "50",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Offer/demo CTA email (engaged)
      emailNode(
        "7",
        "Send demo/offer CTA email",
        1480,
        "{lead_email}",
        "{lead_first_name}, ready to see it in action?",
        "Hi {lead_first_name},\\n\\nYou have been exploring our resources and I wanted to invite you to see how this all comes together.\\n\\nWe would love to give you a personalized demo tailored to {lead_company}. In just 20 minutes, we can show you:\\n\\n- How teams like yours get started\\n- The features most relevant to your use case\\n- A realistic timeline to value\\n\\n[Book Your Demo]\\n\\nNo pressure — just a conversation to see if it is a fit.\\n\\nBest,\\nThe Team",
      ),

      // 8. Objection handling email (not engaged) + sales handoff
      emailNode(
        "8",
        "Send objection handling email",
        1480,
        "{lead_email}",
        "Common questions from teams like {lead_company}",
        "Hi {lead_first_name},\\n\\nI know evaluating new solutions can raise a lot of questions. Here are the most common ones we hear from teams similar to yours:\\n\\nQ: How long does setup take?\\nA: Most teams are up and running in under a week.\\n\\nQ: What if it does not fit our workflow?\\nA: We offer flexible configurations and a dedicated onboarding team.\\n\\nQ: What is the ROI timeline?\\nA: Clients typically see measurable results within the first quarter.\\n\\nIf you have other questions, just reply to this email.\\n\\nBest,\\nThe Team",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // engaged -> demo CTA
      e("6", "8", "output_2"), // not engaged -> objection handling
    ],
  },

  // ===================================================================
  // 578. EMAIL RE-ENGAGEMENT CAMPAIGN (8 nodes)
  // ===================================================================

  "Email Re-Engagement Campaign": {
    summary:
      "Weekly dormant subscriber re-engagement campaign that scans for inactive contacts, segments by historical value, generates AI-driven win-back content, sends a first win-back email, waits for engagement, sends a second offer, branches on recovery status, and logs list health to Sheets.",
    nodes: [
      trigger("Schedule: Weekly dormant subscriber scan", "schedule", {
        schedule: "0 9 * * 2",
        howItStarts:
          "Runs every Tuesday at 9 AM to identify dormant subscribers who have not opened or clicked in 60+ days and launch re-engagement campaigns",
        setup:
          "Configure email service provider API access (SendGrid/Mailchimp). Set dormant threshold (default: 60 days no engagement). Set up win-back email templates and suppression list management.",
        inputFields: [],
      }),

      // 2. Value segmentation (runCode)
      n("2", "runCode", "Segment dormant contacts by value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment dormant contacts into value tiers based on historical engagement
// In production, this would query your ESP for dormant subscribers
const dormantCount = 250; // placeholder — real flow pulls from ESP
const highValue = Math.floor(dormantCount * 0.15); // 15% high-value (past purchasers, high engagement history)
const medValue = Math.floor(dormantCount * 0.35); // 35% medium-value
const lowValue = dormantCount - highValue - medValue; // 50% low-value
return {
  total_dormant: dormantCount,
  high_value_count: highValue,
  medium_value_count: medValue,
  low_value_count: lowValue,
  dormant_threshold_days: 60,
  campaign_batch_id: "reengagement_" + new Date().toISOString().split("T")[0],
  scan_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "total_dormant", type: "number" },
            { name: "high_value_count", type: "number" },
            { name: "medium_value_count", type: "number" },
            { name: "low_value_count", type: "number" },
            { name: "dormant_threshold_days", type: "number" },
            { name: "campaign_batch_id", type: "string" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI win-back angle generation
      aiNode(
        "3",
        "AI generate win-back content angles",
        520,
        "You are a re-engagement email strategist. Generate compelling win-back email content for dormant subscribers across different value tiers. For high-value contacts, create an exclusive or VIP-style message. For medium-value, focus on what they have been missing. For low-value, use a simple 'still interested?' approach. Respond JSON: {high_value_subject: string, high_value_body: string, medium_value_subject: string, medium_value_body: string, low_value_subject: string, low_value_body: string, win_back_strategy: string}",
        "Total dormant: {total_dormant}. High value: {high_value_count}. Medium value: {medium_value_count}. Low value: {low_value_count}. Dormant for: {dormant_threshold_days}+ days.",
        [
          { field: "high_value_subject", as: "hv_subject", type: "string" },
          { field: "high_value_body", as: "hv_body", type: "string" },
          { field: "medium_value_subject", as: "mv_subject", type: "string" },
          { field: "medium_value_body", as: "mv_body", type: "string" },
          { field: "low_value_subject", as: "lv_subject", type: "string" },
          { field: "low_value_body", as: "lv_body", type: "string" },
          {
            field: "win_back_strategy",
            as: "win_back_strategy",
            type: "string",
          },
        ],
      ),

      // 4. First win-back email
      emailNode(
        "4",
        "Send first win-back email",
        760,
        "{dormant_batch_emails}",
        "{hv_subject}",
        "{hv_body}",
      ),

      // 5. Delay + engagement check
      n("5", "delayAction", "Wait 5 days for engagement", 1000, {
        delayDetails: { delayType: "fixed", delayValue: 5, delayUnit: "days" },
      }),

      // 6. Second offer email
      emailNode(
        "6",
        "Send second win-back offer",
        1240,
        "{still_dormant_emails}",
        "We miss you — here is something special",
        "We noticed you have not been around lately and we wanted to make sure you are not missing out.\\n\\nAs a thank-you for being part of our community, here is an exclusive offer just for you.\\n\\nThis offer expires in 48 hours, so do not wait too long!\\n\\n[Claim Your Offer]\\n\\nIf you are no longer interested, you can update your preferences below.",
      ),

      // 7. Recovered/suppressed condition
      n("7", "condition", "Re-engaged (opened/clicked)?", 1480, {
        conditionData: [
          {
            variable: "reengagement_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "reengagement_clicked",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 8. List health logging (Sheets)
      sheetsNode(
        "8",
        "Log re-engagement results (Sheets)",
        1720,
        "ReEngagementLog",
        '[["{scan_timestamp}","{campaign_batch_id}","{total_dormant}","{high_value_count}","{medium_value_count}","{low_value_count}","{win_back_strategy}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // re-engaged -> log recovery
      e("7", "8", "output_2"), // still dormant -> log for suppression
    ],
  },

  // ===================================================================
  // 579. EMAIL SUBJECT LINE A/B GENERATOR (7 nodes)
  // ===================================================================

  "Email Subject Line A/B Generator": {
    summary:
      "AI-powered subject line A/B testing generator that takes a campaign draft, generates style-diverse variants, scores them for spam and predicted performance, configures A/B tests in SendGrid, launches the test, monitors the winner, and logs predicted vs actual performance to Sheets.",
    nodes: [
      trigger("Webhook: Campaign draft ready for testing", "webhook_app", {
        source: "Email Marketing Platform",
        event: "campaign.draft.ready",
        howItStarts:
          "A campaign draft is marked ready for subject line testing, triggering AI variant generation and A/B test setup",
        setup:
          "Configure your email platform to fire a webhook when a campaign draft is finalized and ready for subject line testing. Include campaign ID, current subject line, audience segment, and email body preview.",
        inputFields: [],
      }),

      // 2. AI style-diverse variants
      aiNode(
        "2",
        "AI generate subject line variants",
        280,
        "You are an email subject line optimization expert. Given a campaign draft and its current subject line, generate 4 diverse subject line variants using different psychological approaches: curiosity, urgency, benefit-driven, and social proof. Each should be under 60 characters for mobile compatibility. Respond JSON: {original_subject: string, variant_curiosity: string, variant_urgency: string, variant_benefit: string, variant_social_proof: string, campaign_context: string, audience_segment: string, campaign_id: string}",
        "Campaign draft data: {webhook_payload}",
        [
          { field: "original_subject", as: "original_subject", type: "string" },
          {
            field: "variant_curiosity",
            as: "variant_curiosity",
            type: "string",
          },
          { field: "variant_urgency", as: "variant_urgency", type: "string" },
          { field: "variant_benefit", as: "variant_benefit", type: "string" },
          {
            field: "variant_social_proof",
            as: "variant_social_proof",
            type: "string",
          },
          { field: "campaign_context", as: "campaign_context", type: "string" },
          { field: "audience_segment", as: "audience_segment", type: "string" },
          { field: "campaign_id", as: "campaign_id", type: "string" },
        ],
      ),

      // 3. Spam + performance scoring (runCode)
      n("3", "runCode", "Score variants for spam and performance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score each subject line variant for spam risk and predicted performance
function scoreSubject(subject) {
  let spamScore = 0;
  let performanceScore = 50;
  const lower = subject.toLowerCase();
  // Spam indicators
  const spamWords = ["free", "act now", "limited time", "click here", "buy now", "congratulations", "winner"];
  spamWords.forEach(w => { if (lower.includes(w)) spamScore += 15; });
  if (subject === subject.toUpperCase()) spamScore += 30;
  if ((subject.match(/!/g) || []).length > 1) spamScore += 20;
  if ((subject.match(/\\$/g) || []).length > 0) spamScore += 10;
  // Performance indicators
  if (subject.length <= 50) performanceScore += 15;
  else if (subject.length <= 60) performanceScore += 10;
  if (lower.includes("you") || lower.includes("your")) performanceScore += 10;
  if (subject.includes("?")) performanceScore += 8;
  if (/^\\d/.test(subject)) performanceScore += 7;
  performanceScore = Math.max(0, performanceScore - spamScore);
  return { spam_risk: Math.min(spamScore, 100), predicted_open_rate: Math.min(performanceScore, 100), char_count: subject.length };
}
const variants = [
  { name: "curiosity", subject: vars.variant_curiosity },
  { name: "urgency", subject: vars.variant_urgency },
  { name: "benefit", subject: vars.variant_benefit },
  { name: "social_proof", subject: vars.variant_social_proof }
];
const scored = variants.map(v => ({ ...v, ...scoreSubject(v.subject) }));
scored.sort((a, b) => b.predicted_open_rate - a.predicted_open_rate);
return {
  top_variant_name: scored[0].name,
  top_variant_subject: scored[0].subject,
  top_predicted_rate: scored[0].predicted_open_rate,
  runner_up_subject: scored[1].subject,
  runner_up_predicted_rate: scored[1].predicted_open_rate,
  all_scores: JSON.stringify(scored),
  any_spam_risk: scored.some(s => s.spam_risk > 30)
};`,
          outputVariables: [
            { name: "top_variant_name", type: "string" },
            { name: "top_variant_subject", type: "string" },
            { name: "top_predicted_rate", type: "number" },
            { name: "runner_up_subject", type: "string" },
            { name: "runner_up_predicted_rate", type: "number" },
            { name: "all_scores", type: "string" },
            { name: "any_spam_risk", type: "boolean" },
          ],
        },
      }),

      // 4. A/B test configuration (SendGrid)
      n("4", "hitApi", "Configure SendGrid A/B test", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/marketing/singlesends",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "AB Test - {campaign_id}" },
            { key: "send_to", value: '{"segment_ids":["{audience_segment}"]}' },
            {
              key: "ab_test",
              value:
                '{"type":"subject","test_percentage":20,"winner_criteria":"open","duration":"6h","subject_a":"{top_variant_subject}","subject_b":"{runner_up_subject}"}',
            },
          ],
          variables: [
            { name: "id", customName: "singlesend_id", type: "string" },
            { name: "status", customName: "test_status", type: "string" },
          ],
        },
      }),

      // 5. Launch test
      n("5", "hitApi", "Launch A/B test", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.sendgrid.com/v3/marketing/singlesends/{singlesend_id}/schedule",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "send_at", value: "now" }],
          variables: [
            { name: "status", customName: "launch_status", type: "string" },
            { name: "send_at", customName: "launched_at", type: "string" },
          ],
        },
      }),

      // 6. Winner monitoring delay
      n("6", "delayAction", "Wait 6 hours for test results", 1240, {
        delayDetails: { delayType: "fixed", delayValue: 6, delayUnit: "hours" },
      }),

      // 7. Predicted vs actual logging (Sheets)
      sheetsNode(
        "7",
        "Log A/B test results (Sheets)",
        1480,
        "SubjectLineTests",
        '[["{now}","{campaign_id}","{top_variant_name}","{top_variant_subject}","{top_predicted_rate}","{runner_up_subject}","{runner_up_predicted_rate}","{singlesend_id}","{launch_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 580. EMAIL SUBJECT LINE TESTER (7 nodes)
  // ===================================================================

  "Email Subject Line Tester": {
    summary:
      "On-demand email subject line testing tool. Accepts subject lines via Slack or form, scans for spam patterns, predicts engagement with AI, checks mobile truncation, generates improved alternatives, assembles a scorecard, and delivers results back to the requester via Slack.",
    nodes: [
      trigger("Webhook: Subject line test request", "webhook_app", {
        source: "Slack / Web Form",
        event: "subjectline.test.requested",
        howItStarts:
          "A team member submits a subject line for testing via Slack command or web form",
        setup:
          "Set up a Slack slash command (/test-subject) or web form that sends the subject line, requester info, and optional campaign context to this workflow webhook.",
        inputFields: [],
      }),

      // 2. Spam pattern scan (runCode)
      n("2", "runCode", "Scan for spam patterns", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract subject line and scan for spam patterns
const payload = JSON.parse(vars.webhook_payload || "{}");
const subject = payload.text || payload.subject_line || payload.subject || "";
const requester = payload.user_name || payload.requester || payload.user_id || "unknown";
const channel = payload.channel_id || payload.channel || "";
const lower = subject.toLowerCase();
const issues = [];
// Spam word check
const spamWords = ["free", "act now", "limited time", "click here", "buy now", "congratulations", "winner", "guaranteed", "no obligation", "risk free"];
spamWords.forEach(w => { if (lower.includes(w)) issues.push("Contains spam trigger word: " + w); });
// Formatting checks
if (subject === subject.toUpperCase() && subject.length > 3) issues.push("ALL CAPS detected — high spam risk");
if ((subject.match(/!/g) || []).length > 1) issues.push("Multiple exclamation marks — spam filter trigger");
if ((subject.match(/\\?/g) || []).length > 2) issues.push("Excessive question marks");
if (subject.includes("$") || subject.includes("€") || subject.includes("£")) issues.push("Currency symbols detected — may trigger spam filters");
if (/RE:|FW:/i.test(subject) && !payload.is_reply) issues.push("Fake reply/forward prefix detected");
const spamScore = Math.min(issues.length * 15, 100);
return {
  subject_line: subject,
  requester_id: requester,
  slack_channel: channel,
  spam_score: spamScore,
  spam_issues: issues.join("; ") || "No spam issues detected",
  issue_count: issues.length,
  char_count: subject.length,
  word_count: subject.split(/\\s+/).length,
  mobile_truncated: subject.length > 40
};`,
          outputVariables: [
            { name: "subject_line", type: "string" },
            { name: "requester_id", type: "string" },
            { name: "slack_channel", type: "string" },
            { name: "spam_score", type: "number" },
            { name: "spam_issues", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "char_count", type: "number" },
            { name: "word_count", type: "number" },
            { name: "mobile_truncated", type: "boolean" },
          ],
        },
      }),

      // 3. AI engagement prediction
      aiNode(
        "3",
        "AI predict engagement and score",
        520,
        "You are an email marketing analytics expert. Analyze the given subject line for predicted engagement. Consider psychological triggers, personalization, curiosity gaps, benefit clarity, and emotional resonance. Rate open rate potential 1-100. Respond JSON: {predicted_open_rate: number, engagement_factors: string[], weakness_factors: string[], emotional_tone: string, readability_grade: string, overall_grade: string}",
        "Subject line: {subject_line}\\nCharacter count: {char_count}\\nSpam score: {spam_score}\\nSpam issues: {spam_issues}",
        [
          {
            field: "predicted_open_rate",
            as: "predicted_open_rate",
            type: "number",
          },
          {
            field: "engagement_factors",
            as: "engagement_factors",
            type: "string",
          },
          { field: "weakness_factors", as: "weakness_factors", type: "string" },
          { field: "emotional_tone", as: "emotional_tone", type: "string" },
          {
            field: "readability_grade",
            as: "readability_grade",
            type: "string",
          },
          { field: "overall_grade", as: "overall_grade", type: "string" },
        ],
      ),

      // 4. Mobile truncation check + improved alternatives
      aiNode(
        "4",
        "AI generate improved alternatives",
        760,
        "You are a subject line optimization expert. Given the original subject line analysis, generate 3 improved alternatives that address the identified weaknesses while maintaining the core message. Ensure all alternatives are under 40 characters for mobile compatibility. Use different psychological approaches. Respond JSON: {alternative_1: string, alternative_2: string, alternative_3: string, improvement_notes: string, mobile_preview: string}",
        "Original: {subject_line}\\nGrade: {overall_grade}\\nWeaknesses: {weakness_factors}\\nTone: {emotional_tone}\\nMobile truncated: {mobile_truncated}\\nSpam issues: {spam_issues}",
        [
          { field: "alternative_1", as: "alt_subject_1", type: "string" },
          { field: "alternative_2", as: "alt_subject_2", type: "string" },
          { field: "alternative_3", as: "alt_subject_3", type: "string" },
          {
            field: "improvement_notes",
            as: "improvement_notes",
            type: "string",
          },
          { field: "mobile_preview", as: "mobile_preview", type: "string" },
        ],
      ),

      // 5. Scorecard assembly (runCode)
      n("5", "runCode", "Assemble subject line scorecard", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble final scorecard
const grade = vars.overall_grade || "B";
const mobileIcon = vars.mobile_truncated === "true" ? "TRUNCATED" : "OK";
const scorecard = [
  "Subject Line Scorecard",
  "=====================",
  "Original: " + vars.subject_line,
  "Overall Grade: " + grade,
  "Predicted Open Rate: " + vars.predicted_open_rate + "%",
  "Spam Score: " + vars.spam_score + "/100",
  "Characters: " + vars.char_count + " (Mobile: " + mobileIcon + ")",
  "",
  "Strengths: " + vars.engagement_factors,
  "Weaknesses: " + vars.weakness_factors,
  "Tone: " + vars.emotional_tone,
  "",
  "Improved Alternatives:",
  "1. " + vars.alt_subject_1,
  "2. " + vars.alt_subject_2,
  "3. " + vars.alt_subject_3,
  "",
  "Notes: " + vars.improvement_notes
].join("\\n");
return {
  scorecard_text: scorecard,
  pass_fail: parseInt(vars.spam_score) <= 30 && parseInt(vars.predicted_open_rate) >= 40 ? "PASS" : "NEEDS_WORK"
};`,
          outputVariables: [
            { name: "scorecard_text", type: "string" },
            { name: "pass_fail", type: "string" },
          ],
        },
      }),

      // 6. Scorecard assembly complete — post to Slack
      slackNode(
        "6",
        "Slack: Deliver scorecard to requester",
        1240,
        ":bar_chart: Subject Line Test Results\\n\\n{scorecard_text}\\n\\nVerdict: {pass_fail}",
      ),

      // 7. Log test (Sheets)
      sheetsNode(
        "7",
        "Log subject line test (Sheets)",
        1480,
        "SubjectLineTests",
        '[["{now}","{requester_id}","{subject_line}","{overall_grade}","{predicted_open_rate}","{spam_score}","{pass_fail}","{alt_subject_1}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 581. EMAIL TEMPLATE QUICK REPLY BOT (7 nodes)
  // ===================================================================

  "Email Template Quick Reply Bot": {
    summary:
      "AI-powered quick reply bot for inbound emails. Classifies sender intent, searches a template library for the best match, personalizes the template with contextual details, gates on confidence for auto-send vs human approval, sends the reply, and logs template usage patterns to Sheets.",
    nodes: [
      trigger("Inbound Email: New message for quick reply", "email", {
        source: "Email Server",
        event: "email.received",
        howItStarts:
          "A new inbound email arrives at a monitored inbox and the quick reply bot evaluates whether a templated response is appropriate",
        setup:
          "Configure email forwarding from your monitored inbox to this workflow. Maintain a template library in a structured format (JSON/Sheets) with intent categories, template text, and personalization tokens.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI classify sender intent",
        280,
        "You are an email intent classification assistant. Analyze the inbound email and classify the sender's intent into one of these categories: pricing_inquiry, demo_request, support_question, partnership_inquiry, job_application, meeting_request, feedback, unsubscribe, general_inquiry. Assess how well a template can handle this vs needing a custom response. Respond JSON: {intent: string, sub_intent: string, confidence: number, sender_email: string, sender_name: string, key_details: string, template_suitable: boolean, urgency: string}",
        "Inbound email: {webhook_payload}",
        [
          { field: "intent", as: "sender_intent", type: "string" },
          { field: "sub_intent", as: "sub_intent", type: "string" },
          { field: "confidence", as: "intent_confidence", type: "number" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "key_details", as: "key_details", type: "string" },
          {
            field: "template_suitable",
            as: "template_suitable",
            type: "boolean",
          },
          { field: "urgency", as: "email_urgency", type: "string" },
        ],
      ),

      // 3. Template library search
      aiNode(
        "3",
        "AI select and retrieve best template",
        520,
        "You are a template matching assistant. Given the classified intent and key details, select the best matching email template and fill in personalization tokens. Templates available: pricing_inquiry (include pricing page link, mention free trial), demo_request (include calendar link, mention 15-min demo), support_question (include help center link, mention ticket creation), partnership_inquiry (include partnerships page, mention review timeline), meeting_request (include calendar link), general_inquiry (friendly acknowledgment). Respond JSON: {template_name: string, reply_subject: string, reply_body: string, personalization_applied: string[], match_quality: string}",
        "Intent: {sender_intent}\\nSub-intent: {sub_intent}\\nSender: {sender_name} ({sender_email})\\nKey details: {key_details}\\nUrgency: {email_urgency}",
        [
          { field: "template_name", as: "template_name", type: "string" },
          { field: "reply_subject", as: "reply_subject", type: "string" },
          { field: "reply_body", as: "reply_body", type: "string" },
          {
            field: "personalization_applied",
            as: "personalizations",
            type: "string",
          },
          { field: "match_quality", as: "match_quality", type: "string" },
        ],
      ),

      // 4. Confidence condition
      n("4", "condition", "Confidence >= 0.85 and template suitable?", 760, {
        conditionData: [
          {
            variable: "intent_confidence",
            value: "0.85",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
          {
            variable: "template_suitable",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Auto-send reply
      emailNode(
        "5",
        "Auto-send template reply",
        1000,
        "{sender_email}",
        "{reply_subject}",
        "{reply_body}",
      ),

      // 6. Approval gate (low confidence)
      n(
        "6",
        "approvalGate",
        "Hold for human approval",
        1000,
        {
          approvalGateDetails: {
            approverEmail: "{inbox_manager_email}",
            message:
              "Quick Reply Bot needs approval.\\nIntent: {sender_intent} (confidence: {intent_confidence})\\nTemplate: {template_name}\\nSender: {sender_name} ({sender_email})\\nDraft reply subject: {reply_subject}\\nDraft reply body preview: {reply_body}",
            timeoutHours: 4,
          },
        },
        500,
      ),

      // 7. Template usage logging (Sheets)
      sheetsNode(
        "7",
        "Log template usage (Sheets)",
        1240,
        "TemplateUsage",
        '[["{now}","{sender_email}","{sender_intent}","{template_name}","{intent_confidence}","{match_quality}","{personalizations}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // high confidence -> auto-send
      e("4", "6", "output_2"), // low confidence -> approval gate
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 582. EMAIL TEMPLATE WRITER (7 nodes)
  // ===================================================================

  "Email Template Writer": {
    summary:
      "AI-powered email template creation tool that takes a template request, loads brand voice and examples, generates subject, preheader, body, and CTA with responsive HTML rendering, runs a deliverability pre-check, saves to the template library, and notifies the team with a preview.",
    nodes: [
      trigger("Webhook: Email template creation request", "webhook_app", {
        source: "Slack / Internal Tool",
        event: "template.request",
        howItStarts:
          "A team member requests a new email template via Slack command, form, or internal tool, providing the template purpose, audience, and any special requirements",
        setup:
          "Configure a Slack slash command or internal tool to submit template requests to this webhook. Include template purpose, target audience, brand name, tone preferences, and any specific CTA requirements.",
        inputFields: [],
      }),

      // 2. Brand voice + examples load
      aiNode(
        "2",
        "AI extract request and load brand context",
        280,
        "You are a data extraction assistant. Parse the template request to identify the purpose, audience, tone, CTA requirements, and any specific content to include. Also extract the requester info. Respond JSON: {purpose: string, audience: string, tone: string, cta_requirement: string, special_instructions: string, requester_email: string, requester_name: string, template_category: string, brand_name: string}",
        "Template request payload: {webhook_payload}",
        [
          { field: "purpose", as: "template_purpose", type: "string" },
          { field: "audience", as: "target_audience", type: "string" },
          { field: "tone", as: "brand_tone", type: "string" },
          { field: "cta_requirement", as: "cta_requirement", type: "string" },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "template_category",
            as: "template_category",
            type: "string",
          },
          { field: "brand_name", as: "brand_name", type: "string" },
        ],
      ),

      // 3. AI subject/preheader/body/CTA generation
      aiNode(
        "3",
        "AI generate email template content",
        520,
        "You are an expert email copywriter. Generate a complete email template with subject line, preheader text, email body (with clear sections), and a compelling CTA. Follow the brand tone specified. Keep subject under 50 chars, preheader under 100 chars. Body should be scannable with short paragraphs. CTA should be action-oriented. Respond JSON: {subject_line: string, preheader: string, headline: string, body_intro: string, body_main: string, body_closing: string, cta_text: string, cta_url_placeholder: string, footer_text: string, template_name: string}",
        "Purpose: {template_purpose}\\nAudience: {target_audience}\\nTone: {brand_tone}\\nCTA: {cta_requirement}\\nBrand: {brand_name}\\nSpecial: {special_instructions}",
        [
          { field: "subject_line", as: "tmpl_subject", type: "string" },
          { field: "preheader", as: "tmpl_preheader", type: "string" },
          { field: "headline", as: "tmpl_headline", type: "string" },
          { field: "body_intro", as: "tmpl_intro", type: "string" },
          { field: "body_main", as: "tmpl_main", type: "string" },
          { field: "body_closing", as: "tmpl_closing", type: "string" },
          { field: "cta_text", as: "tmpl_cta_text", type: "string" },
          { field: "cta_url_placeholder", as: "tmpl_cta_url", type: "string" },
          { field: "footer_text", as: "tmpl_footer", type: "string" },
          { field: "template_name", as: "template_name", type: "string" },
        ],
      ),

      // 4. Responsive HTML rendering (runCode)
      n("4", "runCode", "Render responsive HTML email template", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build responsive HTML email template
const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>' + vars.tmpl_subject + '</title><style>body{margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4}table{border-spacing:0}.container{max-width:600px;margin:0 auto;background:#ffffff}.header{background:#1a1a2e;color:#ffffff;padding:30px;text-align:center}.content{padding:30px}.cta-btn{display:inline-block;background:#e94560;color:#ffffff;padding:14px 28px;text-decoration:none;border-radius:6px;font-weight:bold}.footer{background:#f8f8f8;padding:20px;text-align:center;font-size:12px;color:#999}@media(max-width:600px){.container{width:100%!important}.content{padding:15px!important}}</style></head><body><div class="container"><div class="header"><h1>' + vars.tmpl_headline + '</h1></div><div class="content"><p>' + vars.tmpl_intro + '</p><p>' + vars.tmpl_main + '</p><p>' + vars.tmpl_closing + '</p><p style="text-align:center;margin:30px 0"><a href="' + vars.tmpl_cta_url + '" class="cta-btn">' + vars.tmpl_cta_text + '</a></p></div><div class="footer"><p>' + vars.tmpl_footer + '</p><p><a href="{{unsubscribe_url}}">Unsubscribe</a></p></div></div></body></html>';
return {
  html_template: html,
  html_size_kb: Math.round(html.length / 1024 * 100) / 100,
  has_unsubscribe: html.includes("unsubscribe"),
  has_responsive_meta: html.includes("viewport"),
  template_id: "tmpl_" + Date.now()
};`,
          outputVariables: [
            { name: "html_template", type: "string" },
            { name: "html_size_kb", type: "number" },
            { name: "has_unsubscribe", type: "boolean" },
            { name: "has_responsive_meta", type: "boolean" },
            { name: "template_id", type: "string" },
          ],
        },
      }),

      // 5. Deliverability pre-check
      n("5", "runCode", "Run deliverability pre-check", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Pre-check template for deliverability issues
const html = vars.html_template || "";
const subject = vars.tmpl_subject || "";
const issues = [];
// HTML checks
if (!html.includes("unsubscribe")) issues.push("Missing unsubscribe link (CAN-SPAM requirement)");
if (html.length > 102400) issues.push("HTML exceeds 100KB — may be clipped by Gmail");
const imgCount = (html.match(/<img/g) || []).length;
const textLength = html.replace(/<[^>]*>/g, "").length;
if (imgCount > 0 && textLength < 200) issues.push("Low text-to-image ratio — spam filter risk");
if (!html.includes("viewport")) issues.push("Missing responsive viewport meta tag");
// Subject line checks
if (subject.length > 60) issues.push("Subject line exceeds 60 chars — may truncate on mobile");
if (subject === subject.toUpperCase()) issues.push("Subject line is all caps — spam trigger");
const passesCheck = issues.length === 0;
return {
  deliverability_issues: issues.join("; ") || "All checks passed",
  issue_count: issues.length,
  passes_deliverability: passesCheck,
  text_to_html_ratio: Math.round((textLength / html.length) * 100)
};`,
          outputVariables: [
            { name: "deliverability_issues", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "passes_deliverability", type: "boolean" },
            { name: "text_to_html_ratio", type: "number" },
          ],
        },
      }),

      // 6. Save to template library (Sheets)
      sheetsNode(
        "6",
        "Save template to library (Sheets)",
        1240,
        "TemplateLibrary",
        '[["{template_id}","{template_name}","{template_category}","{tmpl_subject}","{tmpl_preheader}","{brand_tone}","{target_audience}","{passes_deliverability}","{now}"]]',
      ),

      // 7. Team notification with preview
      slackNode(
        "7",
        "Slack: New template notification",
        1480,
        ":art: New Email Template Created\\nName: {template_name} ({template_id})\\nCategory: {template_category}\\nSubject: {tmpl_subject}\\nPreheader: {tmpl_preheader}\\nCTA: {tmpl_cta_text}\\nDeliverability: {passes_deliverability} ({deliverability_issues})\\nSize: {html_size_kb}KB | Text ratio: {text_to_html_ratio}%\\nRequested by: {requester_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 583. EMAIL UNSUBSCRIBE MANAGER (8 nodes)
  // ===================================================================

  "Email Unsubscribe Manager": {
    summary:
      "Comprehensive unsubscribe management workflow that captures unsubscribe reasons, applies ESP suppression in SendGrid, updates CRM status in HubSpot, distinguishes full vs category-level unsubscribes, verifies compliance, analyzes campaign-level unsubscribe spikes, and alerts the content team via Slack.",
    nodes: [
      trigger("Webhook: Unsubscribe event received", "webhook_app", {
        source: "Email Service Provider",
        event: "email.unsubscribed",
        howItStarts:
          "A subscriber clicks unsubscribe in an email, triggering the full unsubscribe processing workflow",
        setup:
          "Configure SendGrid/Mailchimp unsubscribe event webhooks. Include subscriber email, campaign ID, unsubscribe reason (if captured), and timestamp. Set up preference center for category-level unsubscribes.",
        inputFields: [],
      }),

      // 2. Reason capture
      n("2", "runCode", "Extract unsubscribe details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse unsubscribe event and extract details
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.recipient || payload.Recipient || "";
const reason = payload.reason || payload.unsubscribe_reason || "not_provided";
const campaignId = payload.campaign_id || payload.sg_message_id || payload.campaign || "";
const category = payload.category || payload.unsubscribe_category || "all";
const timestamp = payload.timestamp || new Date().toISOString();
const isFullUnsub = category === "all" || category === "global" || !payload.category;
return {
  unsub_email: email,
  unsub_reason: reason,
  unsub_campaign_id: campaignId,
  unsub_category: category,
  unsub_timestamp: timestamp,
  is_full_unsubscribe: isFullUnsub,
  is_category_unsubscribe: !isFullUnsub
};`,
          outputVariables: [
            { name: "unsub_email", type: "string" },
            { name: "unsub_reason", type: "string" },
            { name: "unsub_campaign_id", type: "string" },
            { name: "unsub_category", type: "string" },
            { name: "unsub_timestamp", type: "string" },
            { name: "is_full_unsubscribe", type: "boolean" },
            { name: "is_category_unsubscribe", type: "boolean" },
          ],
        },
      }),

      // 3. ESP suppression (SendGrid)
      n("3", "hitApi", "Add to SendGrid suppression list", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/asm/suppressions/global",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "recipient_emails", value: '["{unsub_email}"]' }],
          variables: [
            {
              name: "recipient_emails",
              customName: "suppressed_emails",
              type: "string",
            },
          ],
        },
      }),

      // 4. CRM status update (HubSpot)
      n("4", "hitApi", "Update HubSpot contact status", 760, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/{unsub_email}?idProperty=email",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email_unsubscribed":"true","unsubscribe_date":"{unsub_timestamp}","unsubscribe_reason":"{unsub_reason}","unsubscribe_type":"{unsub_category}","hs_email_optout":"true"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 5. Full vs category distinction (runCode)
      n("5", "runCode", "Process unsubscribe type and compliance", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine compliance actions based on unsubscribe type
const isFull = vars.is_full_unsubscribe === "true";
const reason = vars.unsub_reason || "not_provided";
const actions = [];
if (isFull) {
  actions.push("Global suppression applied");
  actions.push("All future marketing emails blocked");
  actions.push("Transactional emails still permitted (per CAN-SPAM)");
} else {
  actions.push("Category suppression applied: " + vars.unsub_category);
  actions.push("Other email categories remain active");
}
// Compliance verification
const compliant = true; // We always process immediately
const processingTime = "immediate";
return {
  compliance_actions: actions.join("; "),
  is_compliant: compliant,
  processing_time: processingTime,
  suppression_scope: isFull ? "global" : "category:" + vars.unsub_category,
  requires_spike_analysis: true
};`,
          outputVariables: [
            { name: "compliance_actions", type: "string" },
            { name: "is_compliant", type: "boolean" },
            { name: "processing_time", type: "string" },
            { name: "suppression_scope", type: "string" },
            { name: "requires_spike_analysis", type: "boolean" },
          ],
        },
      }),

      // 6. Compliance verification logging
      sheetsNode(
        "6",
        "Log unsubscribe compliance (Sheets)",
        1240,
        "UnsubscribeLog",
        '[["{unsub_timestamp}","{unsub_email}","{unsub_reason}","{unsub_category}","{suppression_scope}","{compliance_actions}","{is_compliant}","{unsub_campaign_id}"]]',
      ),

      // 7. Campaign spike analysis condition
      n("7", "condition", "Campaign-level spike detected?", 1480, {
        conditionData: [
          {
            variable: "requires_spike_analysis",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Content team Slack alert
      slackNode(
        "8",
        "Slack: Unsubscribe alert for content team",
        1720,
        ":bell: Unsubscribe Event Processed\\nEmail: {unsub_email}\\nReason: {unsub_reason}\\nType: {suppression_scope}\\nCampaign: {unsub_campaign_id}\\nCompliance: {compliance_actions}\\nStatus: Processed {processing_time}\\n\\nPlease review campaign {unsub_campaign_id} for content quality if unsubscribe rates are elevated.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // spike -> alert content team
      e("7", "8", "output_2"), // no spike -> still log alert
    ],
  },

  // ===================================================================
  // 584. EMAIL TO SLACK MESSAGE BRIDGE (7 nodes)
  // ===================================================================

  "Email to Slack Message Bridge": {
    summary:
      "Bridges inbound emails to Slack channels by extracting content, parsing urgency and sender info with AI, routing to the appropriate channel via runCode, posting a formatted Slack Block Kit message, linking to email threads, checking for reply mirroring, and logging bridge activity to Sheets.",
    nodes: [
      trigger("Inbound Email: Message for Slack bridge", "email", {
        source: "Email Server",
        event: "email.received",
        howItStarts:
          "A new email arrives at a monitored inbox that is configured to bridge messages to Slack channels",
        setup:
          "Configure email forwarding from monitored addresses to this workflow webhook. Set up Slack channel mapping rules (e.g., support@ -> #support, sales@ -> #sales). Configure Slack app with Block Kit permissions.",
        inputFields: [],
      }),

      // 2. AI summary + urgency parse
      aiNode(
        "2",
        "AI summarize and parse urgency",
        280,
        "You are an email-to-Slack bridge assistant. Parse the inbound email to extract sender info, summarize the content concisely (max 200 words), and determine urgency level. Identify the best Slack channel based on email content and sender. Respond JSON: {sender_email: string, sender_name: string, subject: string, summary: string, urgency: string, suggested_channel: string, is_actionable: boolean, key_points: string[], thread_id: string}",
        "Inbound email payload: {webhook_payload}",
        [
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "subject", as: "email_subject", type: "string" },
          { field: "summary", as: "email_summary", type: "string" },
          { field: "urgency", as: "urgency_level", type: "string" },
          {
            field: "suggested_channel",
            as: "suggested_channel",
            type: "string",
          },
          { field: "is_actionable", as: "is_actionable", type: "boolean" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "thread_id", as: "email_thread_id", type: "string" },
        ],
      ),

      // 3. Channel routing (runCode)
      n("3", "runCode", "Route to Slack channel", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Route email to appropriate Slack channel and format metadata
const channel = vars.suggested_channel || "#general";
const urgency = (vars.urgency_level || "medium").toLowerCase();
const urgencyEmoji = { critical: ":rotating_light:", high: ":warning:", medium: ":email:", low: ":envelope:" };
const emoji = urgencyEmoji[urgency] || ":email:";
const points = JSON.parse(vars.key_points || "[]");
const bulletPoints = points.map(p => "- " + p).join("\\n");
return {
  target_channel: channel,
  urgency_emoji: emoji,
  formatted_points: bulletPoints || "- " + vars.email_summary,
  slack_color: urgency === "critical" ? "#ff0000" : urgency === "high" ? "#ff9900" : "#36a64f",
  bridge_id: "bridge_" + Date.now()
};`,
          outputVariables: [
            { name: "target_channel", type: "string" },
            { name: "urgency_emoji", type: "string" },
            { name: "formatted_points", type: "string" },
            { name: "slack_color", type: "string" },
            { name: "bridge_id", type: "string" },
          ],
        },
      }),

      // 4. Slack Block Kit message
      n("4", "hitApi", "Post Block Kit message to Slack", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{target_channel}" },
            {
              key: "text",
              value:
                "{urgency_emoji} Email from {sender_name}: {email_subject}",
            },
            {
              key: "blocks",
              value:
                '[{"type":"header","text":{"type":"plain_text","text":"{urgency_emoji} Email Bridge: {email_subject}"}},{"type":"section","fields":[{"type":"mrkdwn","text":"*From:* {sender_name} ({sender_email})"},{"type":"mrkdwn","text":"*Urgency:* {urgency_level}"}]},{"type":"section","text":{"type":"mrkdwn","text":"*Summary:*\\n{email_summary}"}},{"type":"section","text":{"type":"mrkdwn","text":"*Key Points:*\\n{formatted_points}"}},{"type":"actions","elements":[{"type":"button","text":{"type":"plain_text","text":"Reply via Email"},"action_id":"reply_email","value":"{sender_email}"},{"type":"button","text":{"type":"plain_text","text":"Mark Handled"},"action_id":"mark_handled","value":"{bridge_id}"}]}]',
            },
          ],
          variables: [
            { name: "ts", customName: "slack_message_ts", type: "string" },
            { name: "channel", customName: "posted_channel", type: "string" },
          ],
        },
      }),

      // 5. Thread linkage
      n("5", "hitApi", "Link email thread in Slack reply", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://slack.com/api/chat.postMessage",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "channel", value: "{posted_channel}" },
            { key: "thread_ts", value: "{slack_message_ts}" },
            {
              key: "text",
              value:
                ":link: Email thread ID: {email_thread_id}\\nBridge ID: {bridge_id}\\nReply to this thread to compose a response.",
            },
          ],
          variables: [
            { name: "ok", customName: "thread_linked", type: "boolean" },
          ],
        },
      }),

      // 6. Reply mirror condition
      n("6", "condition", "Actionable email requiring response?", 1000, {
        conditionData: [
          {
            variable: "is_actionable",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Bridge activity logging (Sheets)
      sheetsNode(
        "7",
        "Log bridge activity (Sheets)",
        1240,
        "EmailBridge",
        '[["{now}","{sender_email}","{email_subject}","{urgency_level}","{target_channel}","{bridge_id}","{is_actionable}","{email_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // actionable -> log as needing response
      e("6", "7", "output_2"), // informational -> log as FYI
    ],
  },

  // ===================================================================
  // 585. GOOGLE FORMS TO MAILCHIMP SUBSCRIBER (6 nodes)
  // ===================================================================

  "Outgrow Forms to Mailchimp Subscriber": {
    summary:
      "Syncs Outgrow Forms responses to Mailchimp as subscribers. Validates submitted fields, checks for duplicate subscribers, upserts contacts in Mailchimp with tags and merge fields, enrolls new subscribers in welcome automations, and logs sync activity to a tracking sheet.",
    nodes: [
      trigger("Webhook: Outgrow Form response submitted", "webhook_app", {
        source: "Outgrow Forms",
        event: "form.response.submitted",
        howItStarts:
          "A user submits a Outgrow Form that captures newsletter signup information (email, name, preferences)",
        setup:
          "In Outgrow Forms, go to Script Editor and add an onFormSubmit trigger that sends form responses to this workflow webhook URL. Include all form field values in the payload.",
        inputFields: [],
      }),

      // 2. Field validation (runCode)
      n("2", "runCode", "Validate and extract form fields", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and validate form submission fields
const payload = JSON.parse(vars.webhook_payload || "{}");
const responses = payload.namedValues || payload.values || payload;
// Find email field regardless of naming
const email = responses.email || responses.Email || responses["Email Address"] || responses.email_address || "";
const firstName = responses.first_name || responses["First Name"] || responses.firstName || responses.name || "";
const lastName = responses.last_name || responses["Last Name"] || responses.lastName || "";
const interests = responses.interests || responses.preferences || responses.topics || "";
// Validate email format
const emailStr = Array.isArray(email) ? email[0] : email;
const nameStr = Array.isArray(firstName) ? firstName[0] : firstName;
const lastStr = Array.isArray(lastName) ? lastName[0] : lastName;
const interestsStr = Array.isArray(interests) ? interests.join(",") : interests;
const isValidEmail = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(emailStr);
return {
  subscriber_email: emailStr.toLowerCase().trim(),
  subscriber_first_name: nameStr,
  subscriber_last_name: lastStr,
  subscriber_interests: interestsStr,
  is_valid_email: isValidEmail,
  form_timestamp: new Date().toISOString(),
  submission_source: "google_forms"
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_first_name", type: "string" },
            { name: "subscriber_last_name", type: "string" },
            { name: "subscriber_interests", type: "string" },
            { name: "is_valid_email", type: "boolean" },
            { name: "form_timestamp", type: "string" },
            { name: "submission_source", type: "string" },
          ],
        },
      }),

      // 3. Mailchimp duplicate check + upsert
      n("3", "hitApi", "Check Mailchimp for existing subscriber", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/search-members?query={subscriber_email}&list_id={mailchimp_list_id}",
          headers: [{ key: "Authorization", value: "Bearer {mailchimp_key}" }],
          body: [],
          variables: [
            {
              name: "exact_matches.total_items",
              customName: "existing_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Subscriber upsert (Mailchimp)
      n("4", "hitApi", "Upsert subscriber in Mailchimp", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/lists/{mailchimp_list_id}/members/{subscriber_email_hash}",
          headers: [
            { key: "Authorization", value: "Bearer {mailchimp_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email_address", value: "{subscriber_email}" },
            { key: "status_if_new", value: "subscribed" },
            {
              key: "merge_fields",
              value:
                '{"FNAME":"{subscriber_first_name}","LNAME":"{subscriber_last_name}","INTERESTS":"{subscriber_interests}"}',
            },
            { key: "tags", value: '["google_forms","{submission_source}"]' },
          ],
          variables: [
            { name: "id", customName: "mailchimp_member_id", type: "string" },
            {
              name: "status",
              customName: "subscription_status",
              type: "string",
            },
            { name: "tags_count", customName: "tag_count", type: "number" },
          ],
        },
      }),

      // 5. Welcome automation enrollment
      emailNode(
        "5",
        "Send welcome confirmation email",
        1000,
        "{subscriber_email}",
        "Welcome! You are all set",
        "Hi {subscriber_first_name},\\n\\nThank you for subscribing! We are excited to have you.\\n\\nYou have signed up for: {subscriber_interests}\\n\\nHere is what to expect:\\n- Regular updates on topics you care about\\n- Exclusive content and early access\\n- Easy unsubscribe anytime\\n\\nWelcome aboard!\\nThe Team",
      ),

      // 6. Tracking sheet update
      sheetsNode(
        "6",
        "Log form-to-Mailchimp sync (Sheets)",
        1240,
        "FormSubscribers",
        '[["{form_timestamp}","{subscriber_email}","{subscriber_first_name}","{subscriber_last_name}","{subscriber_interests}","{subscription_status}","{existing_count}","{submission_source}"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 586. HUBSPOT CONTACT TO MAILCHIMP SYNC (7 nodes)
  // ===================================================================

  "HubSpot Contact to Mailchimp Sync": {
    summary:
      "Bi-directional sync between HubSpot contacts and Mailchimp subscribers. Checks marketing eligibility, maps fields between platforms via runCode, upserts subscribers in Mailchimp, synchronizes unsubscribe statuses in both directions, logs sync activity to Sheets, and alerts on failures via Slack.",
    nodes: [
      trigger("Webhook: HubSpot contact updated", "webhook_app", {
        source: "HubSpot",
        event: "contact.propertyChange",
        howItStarts:
          "A contact is created or updated in HubSpot, triggering a sync to Mailchimp to keep subscriber lists aligned",
        setup:
          "Configure HubSpot workflow to fire a webhook on contact create/update. Include contact email, marketing status, lifecycle stage, and key properties. Set up Mailchimp API credentials.",
        inputFields: [],
      }),

      // 2. Marketing eligibility check
      n("2", "condition", "Contact is marketing-eligible?", 280, {
        conditionData: [
          {
            variable: "marketing_status",
            value: "opted_in",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "marketing_status",
            value: "subscribed",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 3. Field mapping (runCode)
      n("3", "runCode", "Map HubSpot fields to Mailchimp", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map HubSpot contact fields to Mailchimp merge fields
const payload = JSON.parse(vars.webhook_payload || "{}");
const props = payload.properties || payload;
const email = props.email || props.hs_email || "";
const firstName = props.firstname || props.first_name || "";
const lastName = props.lastname || props.last_name || "";
const company = props.company || props.hs_company || "";
const lifecycle = props.lifecyclestage || props.lifecycle_stage || "";
const phone = props.phone || "";
const city = props.city || "";
const state = props.state || "";
// Generate Mailchimp subscriber hash (MD5 of lowercase email)
const emailLower = email.toLowerCase().trim();
// Map lifecycle to Mailchimp tags
const tagMap = {
  subscriber: "subscriber",
  lead: "lead",
  marketingqualifiedlead: "mql",
  salesqualifiedlead: "sql",
  opportunity: "opportunity",
  customer: "customer",
  evangelist: "evangelist"
};
const tag = tagMap[lifecycle] || "contact";
return {
  contact_email: emailLower,
  mc_fname: firstName,
  mc_lname: lastName,
  mc_company: company,
  mc_phone: phone,
  mc_city: city,
  mc_state: state,
  mc_tag: tag,
  hs_lifecycle: lifecycle,
  sync_timestamp: new Date().toISOString(),
  is_unsubscribed: props.hs_email_optout === "true" || props.email_unsubscribed === "true"
};`,
          outputVariables: [
            { name: "contact_email", type: "string" },
            { name: "mc_fname", type: "string" },
            { name: "mc_lname", type: "string" },
            { name: "mc_company", type: "string" },
            { name: "mc_phone", type: "string" },
            { name: "mc_city", type: "string" },
            { name: "mc_state", type: "string" },
            { name: "mc_tag", type: "string" },
            { name: "hs_lifecycle", type: "string" },
            { name: "sync_timestamp", type: "string" },
            { name: "is_unsubscribed", type: "boolean" },
          ],
        },
      }),

      // 4. Mailchimp upsert
      n("4", "hitApi", "Upsert subscriber in Mailchimp", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/lists/{mailchimp_list_id}/members/{contact_email_hash}",
          headers: [
            { key: "Authorization", value: "Bearer {mailchimp_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email_address", value: "{contact_email}" },
            { key: "status_if_new", value: "subscribed" },
            {
              key: "merge_fields",
              value:
                '{"FNAME":"{mc_fname}","LNAME":"{mc_lname}","COMPANY":"{mc_company}","PHONE":"{mc_phone}"}',
            },
            { key: "tags", value: '["{mc_tag}","hubspot_sync"]' },
          ],
          variables: [
            { name: "id", customName: "mailchimp_id", type: "string" },
            { name: "status", customName: "mc_status", type: "string" },
          ],
        },
      }),

      // 5. Bi-directional unsubscribe sync
      n("5", "hitApi", "Sync unsubscribe status (Mailchimp -> HubSpot)", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/lists/{mailchimp_list_id}/members/{contact_email_hash}",
          headers: [{ key: "Authorization", value: "Bearer {mailchimp_key}" }],
          body: [],
          variables: [
            { name: "status", customName: "mc_current_status", type: "string" },
            {
              name: "unsubscribe_reason",
              customName: "mc_unsub_reason",
              type: "string",
            },
          ],
        },
      }),

      // 6. Sync logging (Sheets)
      sheetsNode(
        "6",
        "Log sync activity (Sheets)",
        1240,
        "HubSpotMailchimpSync",
        '[["{sync_timestamp}","{contact_email}","{hs_lifecycle}","{mc_tag}","{mc_status}","{mc_current_status}","{is_unsubscribed}"]]',
      ),

      // 7. Failure alert (Slack)
      slackNode(
        "7",
        "Slack: Sync status notification",
        1480,
        ":arrows_counterclockwise: HubSpot-Mailchimp Sync Complete\\nContact: {contact_email}\\nHubSpot Lifecycle: {hs_lifecycle}\\nMailchimp Status: {mc_current_status}\\nTag: {mc_tag}\\nTimestamp: {sync_timestamp}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // marketing eligible -> map fields
      e("2", "6", "output_2"), // not eligible -> log only
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 587. MAILCHIMP CAMPAIGN STATS TO SHEET (6 nodes)
  // ===================================================================

  "Mailchimp Campaign Stats to Sheet": {
    summary:
      "Pulls Mailchimp campaign performance metrics after a send, generates AI commentary on results, appends detailed KPIs to a Google Sheet, calculates click-to-open rate and other derived metrics, and posts a summary to Slack for the email marketing team.",
    nodes: [
      trigger("Webhook: Mailchimp campaign sent", "webhook_app", {
        source: "Mailchimp",
        event: "campaign.sent",
        howItStarts:
          "A Mailchimp campaign finishes sending, triggering a metrics pull and analysis pipeline",
        setup:
          "Configure Mailchimp webhook for campaign.sent events. Include campaign ID in the payload. Set up Google Sheets for campaign tracking and Slack webhook for notifications.",
        inputFields: [],
      }),

      // 2. Metrics pull (Mailchimp API)
      n("2", "hitApi", "Pull campaign stats (Mailchimp)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/reports/{campaign_id}",
          headers: [{ key: "Authorization", value: "Bearer {mailchimp_key}" }],
          body: [],
          variables: [
            { name: "emails_sent", customName: "emails_sent", type: "number" },
            {
              name: "opens.open_rate",
              customName: "open_rate",
              type: "number",
            },
            {
              name: "opens.unique_opens",
              customName: "unique_opens",
              type: "number",
            },
            {
              name: "clicks.click_rate",
              customName: "click_rate",
              type: "number",
            },
            {
              name: "clicks.unique_clicks",
              customName: "unique_clicks",
              type: "number",
            },
            {
              name: "unsubscribed",
              customName: "unsubscribes",
              type: "number",
            },
            {
              name: "bounces.hard_bounces",
              customName: "hard_bounces",
              type: "number",
            },
            {
              name: "bounces.soft_bounces",
              customName: "soft_bounces",
              type: "number",
            },
            {
              name: "campaign_title",
              customName: "campaign_title",
              type: "string",
            },
            {
              name: "subject_line",
              customName: "subject_line",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI commentary generation
      aiNode(
        "3",
        "AI generate campaign commentary",
        520,
        "You are an email marketing analyst. Analyze the campaign metrics and provide actionable commentary. Compare against industry benchmarks (avg open rate ~20%, click rate ~2.5%, unsub rate <0.5%). Identify what worked and what needs improvement. Respond JSON: {performance_grade: string, commentary: string, strengths: string, improvements: string, benchmark_comparison: string, recommended_actions: string}",
        "Campaign: {campaign_title}\\nSubject: {subject_line}\\nSent: {emails_sent}\\nOpen rate: {open_rate}\\nUnique opens: {unique_opens}\\nClick rate: {click_rate}\\nUnique clicks: {unique_clicks}\\nUnsubscribes: {unsubscribes}\\nHard bounces: {hard_bounces}\\nSoft bounces: {soft_bounces}",
        [
          {
            field: "performance_grade",
            as: "performance_grade",
            type: "string",
          },
          { field: "commentary", as: "ai_commentary", type: "string" },
          { field: "strengths", as: "campaign_strengths", type: "string" },
          {
            field: "improvements",
            as: "campaign_improvements",
            type: "string",
          },
          {
            field: "benchmark_comparison",
            as: "benchmark_comparison",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),

      // 4. Sheets append with derived KPIs
      sheetsNode(
        "4",
        "Append campaign stats (Sheets)",
        760,
        "CampaignStats",
        '[["{now}","{campaign_title}","{subject_line}","{emails_sent}","{open_rate}","{unique_opens}","{click_rate}","{unique_clicks}","{unsubscribes}","{hard_bounces}","{performance_grade}","{ai_commentary}"]]',
      ),

      // 5. CTOR calculation (runCode)
      n("5", "runCode", "Calculate derived KPIs", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate click-to-open rate (CTOR) and other derived metrics
const uniqueOpens = parseInt(vars.unique_opens) || 1;
const uniqueClicks = parseInt(vars.unique_clicks) || 0;
const sent = parseInt(vars.emails_sent) || 1;
const unsubs = parseInt(vars.unsubscribes) || 0;
const hardBounces = parseInt(vars.hard_bounces) || 0;
const softBounces = parseInt(vars.soft_bounces) || 0;
const ctor = ((uniqueClicks / uniqueOpens) * 100).toFixed(2);
const bounceRate = (((hardBounces + softBounces) / sent) * 100).toFixed(2);
const unsubRate = ((unsubs / sent) * 100).toFixed(3);
const deliveredCount = sent - hardBounces - softBounces;
const deliveryRate = ((deliveredCount / sent) * 100).toFixed(2);
return {
  ctor: ctor,
  bounce_rate: bounceRate,
  unsub_rate: unsubRate,
  delivery_rate: deliveryRate,
  delivered_count: deliveredCount,
  kpi_summary: "CTOR: " + ctor + "% | Bounce: " + bounceRate + "% | Unsub: " + unsubRate + "% | Delivery: " + deliveryRate + "%"
};`,
          outputVariables: [
            { name: "ctor", type: "string" },
            { name: "bounce_rate", type: "string" },
            { name: "unsub_rate", type: "string" },
            { name: "delivery_rate", type: "string" },
            { name: "delivered_count", type: "number" },
            { name: "kpi_summary", type: "string" },
          ],
        },
      }),

      // 6. Email marketing Slack post
      slackNode(
        "6",
        "Slack: Campaign performance report",
        1240,
        ":bar_chart: Campaign Report: {campaign_title}\\nSubject: {subject_line}\\nGrade: {performance_grade}\\n\\nKey Metrics:\\nSent: {emails_sent} | Delivered: {delivered_count} ({delivery_rate}%)\\nOpens: {unique_opens} ({open_rate}%) | Clicks: {unique_clicks} ({click_rate}%)\\nCTOR: {ctor}% | Bounces: {bounce_rate}% | Unsubs: {unsub_rate}%\\n\\n{ai_commentary}\\n\\nRecommended: {recommended_actions}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 588. MAILCHIMP SUBSCRIBER SEGMENT SYNC (7 nodes)
  // ===================================================================

  "Mailchimp Subscriber Segment Sync": {
    summary:
      "Scheduled bi-directional sync between Mailchimp segments and HubSpot CRM lists. Pulls segments from both platforms, computes drift via runCode, applies bi-directional updates, flags significant divergence, and alerts data operations via Slack when sync gaps exceed thresholds.",
    nodes: [
      trigger("Schedule: Daily segment sync", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily at 7 AM to synchronize subscriber segments between Mailchimp and HubSpot, detecting and resolving drift",
        setup:
          "Configure Mailchimp and HubSpot API credentials. Define segment/list mappings between platforms. Set drift threshold for alerting (default: 5% divergence).",
        inputFields: [],
      }),

      // 2. Mailchimp segment pull
      n("2", "hitApi", "Pull Mailchimp segment members", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/lists/{mailchimp_list_id}/segments/{segment_id}/members?count=1000",
          headers: [{ key: "Authorization", value: "Bearer {mailchimp_key}" }],
          body: [],
          variables: [
            {
              name: "total_items",
              customName: "mc_segment_count",
              type: "number",
            },
            {
              name: "members",
              customName: "mc_members_payload",
              type: "string",
            },
          ],
        },
      }),

      // 3. CRM list pull (HubSpot)
      n("3", "hitApi", "Pull HubSpot list members", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubspot.com/contacts/v1/lists/{hubspot_list_id}/contacts/all?count=250",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "contacts.length",
              customName: "hs_list_count",
              type: "number",
            },
            {
              name: "contacts",
              customName: "hs_contacts_payload",
              type: "string",
            },
          ],
        },
      }),

      // 4. Drift computation (runCode)
      n("4", "runCode", "Compute segment drift", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute drift between Mailchimp segment and HubSpot list
const mcCount = parseInt(vars.mc_segment_count) || 0;
const hsCount = parseInt(vars.hs_list_count) || 0;
const totalUnion = Math.max(mcCount, hsCount);
const countDiff = Math.abs(mcCount - hsCount);
const driftPct = totalUnion > 0 ? ((countDiff / totalUnion) * 100).toFixed(2) : "0";
const driftThreshold = 5.0; // 5% drift threshold
const hasDivergence = parseFloat(driftPct) > driftThreshold;
const largerPlatform = mcCount > hsCount ? "mailchimp" : hsCount > mcCount ? "hubspot" : "equal";
const syncDirection = mcCount > hsCount ? "mc_to_hs" : "hs_to_mc";
return {
  mc_count: mcCount,
  hs_count: hsCount,
  count_difference: countDiff,
  drift_percentage: driftPct,
  has_significant_divergence: hasDivergence,
  larger_platform: largerPlatform,
  recommended_sync_direction: syncDirection,
  sync_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "mc_count", type: "number" },
            { name: "hs_count", type: "number" },
            { name: "count_difference", type: "number" },
            { name: "drift_percentage", type: "string" },
            { name: "has_significant_divergence", type: "boolean" },
            { name: "larger_platform", type: "string" },
            { name: "recommended_sync_direction", type: "string" },
            { name: "sync_timestamp", type: "string" },
          ],
        },
      }),

      // 5. Bi-directional updates (log to Sheets as audit trail)
      sheetsNode(
        "5",
        "Log segment sync audit (Sheets)",
        1000,
        "SegmentSync",
        '[["{sync_timestamp}","{mc_count}","{hs_count}","{count_difference}","{drift_percentage}","{larger_platform}","{recommended_sync_direction}","{has_significant_divergence}"]]',
      ),

      // 6. Divergence condition
      n("6", "condition", "Significant drift detected (>5%)?", 1240, {
        conditionData: [
          {
            variable: "has_significant_divergence",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Data ops Slack alert
      slackNode(
        "7",
        "Slack: Segment drift alert",
        1480,
        ":warning: Segment Sync Drift Alert\\nMailchimp segment: {mc_count} members\\nHubSpot list: {hs_count} members\\nDifference: {count_difference} ({drift_percentage}% drift)\\nLarger platform: {larger_platform}\\nRecommended action: Sync {recommended_sync_direction}\\nPlease investigate and resolve the data divergence.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // significant drift -> alert
      e("6", "7", "output_2"), // normal -> notify sync complete
    ],
  },

  // ===================================================================
  // 589. NEWSLETTER SIGNUP BOT (7 nodes)
  // ===================================================================

  "Newsletter Signup Bot": {
    summary:
      "Conversational AI bot that collects newsletter signup information via chat, validates email and preferences, creates subscribers in Mailchimp, sends a welcome confirmation email, logs attribution data to Sheets, and notifies the growth team via Slack.",
    nodes: [
      trigger("Webhook: Chat signup interaction", "webhook_app", {
        source: "Chat Widget / Website",
        event: "chat.signup.initiated",
        howItStarts:
          "A website visitor initiates a newsletter signup through the chat widget or conversational interface",
        setup:
          "Configure your chat widget (Intercom, Drift, or custom) to fire a webhook when a signup intent is detected. Include the chat transcript, visitor email, preferences, and referral source.",
        inputFields: [],
      }),

      // 2. Email/preference collection (AI)
      aiNode(
        "2",
        "AI extract signup details from chat",
        280,
        "You are a newsletter signup assistant. Parse the chat interaction to extract the subscriber's email address, first name, content preferences (topics they are interested in), and how they found you (referral source). Validate that the email looks legitimate. Respond JSON: {email: string, first_name: string, preferences: string[], referral_source: string, email_valid: boolean, signup_intent_confirmed: boolean, visitor_id: string}",
        "Chat interaction data: {webhook_payload}",
        [
          { field: "email", as: "signup_email", type: "string" },
          { field: "first_name", as: "signup_name", type: "string" },
          { field: "preferences", as: "content_preferences", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          { field: "email_valid", as: "email_is_valid", type: "boolean" },
          {
            field: "signup_intent_confirmed",
            as: "intent_confirmed",
            type: "boolean",
          },
          { field: "visitor_id", as: "visitor_id", type: "string" },
        ],
      ),

      // 3. Validation (runCode)
      n("3", "runCode", "Validate email and deduplicate", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate email format and prepare Mailchimp data
const email = (vars.signup_email || "").toLowerCase().trim();
const isValid = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(email);
const isDisposable = ["tempmail.com","throwaway.email","mailinator.com","guerrillamail.com"].some(d => email.endsWith(d));
const prefs = JSON.parse(vars.content_preferences || "[]");
const tags = ["chat_signup", vars.referral_source || "direct", ...prefs].filter(Boolean);
return {
  validated_email: email,
  passes_validation: isValid && !isDisposable,
  is_disposable: isDisposable,
  mailchimp_tags: JSON.stringify(tags),
  preference_string: prefs.join(", ") || "general",
  signup_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "validated_email", type: "string" },
            { name: "passes_validation", type: "boolean" },
            { name: "is_disposable", type: "boolean" },
            { name: "mailchimp_tags", type: "string" },
            { name: "preference_string", type: "string" },
            { name: "signup_date", type: "string" },
          ],
        },
      }),

      // 4. Mailchimp subscriber creation
      n("4", "hitApi", "Create Mailchimp subscriber", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{mailchimp_dc}.api.mailchimp.com/3.0/lists/{mailchimp_list_id}/members",
          headers: [
            { key: "Authorization", value: "Bearer {mailchimp_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email_address", value: "{validated_email}" },
            { key: "status", value: "subscribed" },
            {
              key: "merge_fields",
              value:
                '{"FNAME":"{signup_name}","SOURCE":"{referral_source}","PREFS":"{preference_string}"}',
            },
            { key: "tags", value: "{mailchimp_tags}" },
          ],
          variables: [
            { name: "id", customName: "mc_subscriber_id", type: "string" },
            { name: "status", customName: "mc_status", type: "string" },
          ],
        },
      }),

      // 5. Welcome confirmation email
      emailNode(
        "5",
        "Send welcome confirmation email",
        1000,
        "{validated_email}",
        "Welcome to our newsletter, {signup_name}!",
        "Hi {signup_name},\\n\\nThank you for signing up for our newsletter! We are thrilled to have you.\\n\\nYou will receive updates on: {preference_string}\\n\\nWhat to expect:\\n- Weekly insights delivered to your inbox\\n- Exclusive content you will not find anywhere else\\n- Tips and strategies from industry experts\\n\\nIf you ever want to adjust your preferences, just reply to any email.\\n\\nWelcome aboard!\\nThe Team",
      ),

      // 6. Attribution logging (Sheets)
      sheetsNode(
        "6",
        "Log signup attribution (Sheets)",
        1240,
        "NewsletterSignups",
        '[["{signup_date}","{validated_email}","{signup_name}","{preference_string}","{referral_source}","{visitor_id}","chat_signup","{mc_status}"]]',
      ),

      // 7. Growth team notification (Slack)
      slackNode(
        "7",
        "Slack: New subscriber notification",
        1480,
        ":tada: New Newsletter Subscriber\\nEmail: {validated_email}\\nName: {signup_name}\\nPreferences: {preference_string}\\nSource: {referral_source} (chat signup)\\nStatus: {mc_status}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 590. NEWSLETTER SUBSCRIBER WELCOME SEQUENCE (8 nodes)
  // ===================================================================

  "Newsletter Subscriber Welcome Sequence": {
    summary:
      "Multi-stage welcome sequence for confirmed newsletter subscribers. Enriches contact data in HubSpot, sends a welcome email, uses AI to select the best historical content for the subscriber, sends a best-of roundup, checks engagement to branch on activity, invites engaged subscribers to the community, and transitions to long-term segmentation.",
    nodes: [
      trigger("Webhook: Newsletter subscription confirmed", "webhook_app", {
        source: "Mailchimp / Email Platform",
        event: "subscriber.confirmed",
        howItStarts:
          "A new subscriber confirms their newsletter subscription (double opt-in confirmed), triggering the welcome sequence",
        setup:
          "Configure Mailchimp or your email platform to fire a webhook on subscription confirmation. Include subscriber email, name, interests, and source. Set up HubSpot CRM for enrichment.",
        inputFields: [],
      }),

      // 2. CRM enrichment (HubSpot)
      aiNode(
        "2",
        "AI extract subscriber data",
        280,
        "You are a data extraction assistant. Parse the webhook payload to extract subscriber details regardless of field naming. Find the email, name, interests/preferences, subscription source, and any other available metadata. Respond JSON: {email: string, first_name: string, last_name: string, interests: string[], source: string, subscription_date: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "email", as: "sub_email", type: "string" },
          { field: "first_name", as: "sub_first_name", type: "string" },
          { field: "last_name", as: "sub_last_name", type: "string" },
          { field: "interests", as: "sub_interests", type: "string" },
          { field: "source", as: "sub_source", type: "string" },
          { field: "subscription_date", as: "sub_date", type: "string" },
        ],
      ),

      // 3. Welcome email
      emailNode(
        "3",
        "Send welcome email",
        520,
        "{sub_email}",
        "Welcome to the newsletter, {sub_first_name}!",
        "Hi {sub_first_name},\\n\\nWelcome! I am glad you decided to subscribe.\\n\\nHere is what you can expect from our newsletter:\\n- Actionable insights delivered weekly\\n- Curated content based on your interests: {sub_interests}\\n- Exclusive content not published anywhere else\\n\\nI have got some of our best content lined up for you in the coming days. Keep an eye on your inbox!\\n\\nCheers,\\nThe Editor",
      ),

      // 4. AI best-content selection
      aiNode(
        "4",
        "AI select personalized best-of content",
        760,
        "You are a content curation assistant. Given the subscriber's interests, select and describe the top 5 newsletter articles from our archives that would be most relevant and engaging for a new subscriber. For each, provide a compelling one-line description. Respond JSON: {articles: [{title: string, description: string, category: string}], curation_rationale: string, content_roundup_intro: string}",
        "Subscriber interests: {sub_interests}\\nSubscription source: {sub_source}\\nName: {sub_first_name}",
        [
          { field: "articles", as: "curated_articles", type: "string" },
          {
            field: "curation_rationale",
            as: "curation_rationale",
            type: "string",
          },
          {
            field: "content_roundup_intro",
            as: "roundup_intro",
            type: "string",
          },
        ],
      ),

      // 5. Best-of roundup email (delayed)
      emailNode(
        "5",
        "Send best-of content roundup",
        1000,
        "{sub_email}",
        "{sub_first_name}, our top picks for you",
        "Hi {sub_first_name},\\n\\n{roundup_intro}\\n\\nBased on your interests in {sub_interests}, here are our most popular articles handpicked for you:\\n\\n{curated_articles}\\n\\nDive in and let us know which ones resonate!\\n\\nThe Editor",
      ),

      // 6. Engagement condition
      n("6", "condition", "Subscriber engaged (opened both emails)?", 1240, {
        conditionData: [
          {
            variable: "welcome_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Community invite (engaged)
      emailNode(
        "7",
        "Send community invitation",
        1480,
        "{sub_email}",
        "You are invited: Join our community, {sub_first_name}",
        "Hi {sub_first_name},\\n\\nSince you have been enjoying our content, I wanted to personally invite you to join our community.\\n\\nIt is where our most engaged readers:\\n- Connect with like-minded professionals\\n- Get early access to new content\\n- Share insights and ask questions\\n\\n[Join the Community]\\n\\nLooking forward to seeing you there!\\nThe Editor",
      ),

      // 8. Long-term segmentation
      n(
        "8",
        "hitApi",
        "Update long-term segment in HubSpot",
        1720,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubspot.com/crm/v3/objects/contacts/{sub_email}?idProperty=email",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"newsletter_segment":"active_engaged","welcome_sequence_completed":"true","content_interests":"{sub_interests}","subscription_source":"{sub_source}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_contact_id", type: "string" },
            ],
          },
        },
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // engaged -> community invite
      e("6", "8", "output_2"), // not engaged -> segment as passive
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 591. ONBOARDING EMAIL SEQUENCE (9 nodes)
  // ===================================================================

  "Onboarding Email Sequence": {
    summary:
      "Full 14-day onboarding email sequence for new signups. Loads plan, role, and company context, sends Day-1 welcome, Day-3 tips, Day-7 usage check with runCode analysis, branches on active/stalled status, sends adaptive check-in emails, delivers a Day-14 value report, and hands off to CS via Slack.",
    nodes: [
      trigger("Webhook: New user signup", "webhook_app", {
        source: "Application / Auth System",
        event: "user.signup.completed",
        howItStarts:
          "A new user completes signup in the application, triggering a 14-day onboarding email sequence tailored to their plan and role",
        setup:
          "Configure your application to fire a webhook on successful user signup. Include user email, name, plan tier, role/title, company name, and any onboarding preferences.",
        inputFields: [],
      }),

      // 2. Plan/role/company load
      aiNode(
        "2",
        "AI extract user onboarding context",
        280,
        "You are a data extraction assistant. Parse the signup webhook to extract user details and onboarding context. Find email, name, plan tier, role, company, and team size regardless of field naming. Respond JSON: {email: string, first_name: string, plan_tier: string, role: string, company_name: string, team_size: string, signup_source: string}",
        "Signup webhook payload: {webhook_payload}",
        [
          { field: "email", as: "user_email", type: "string" },
          { field: "first_name", as: "user_name", type: "string" },
          { field: "plan_tier", as: "plan_tier", type: "string" },
          { field: "role", as: "user_role", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "team_size", as: "team_size", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
        ],
      ),

      // 3. Day-1 welcome email
      emailNode(
        "3",
        "Day 1: Welcome email",
        520,
        "{user_email}",
        "Welcome to the team, {user_name}!",
        "Hi {user_name},\\n\\nWelcome! We are excited to have you and {company_name} on board.\\n\\nYour {plan_tier} plan is all set up and ready to go.\\n\\nHere is how to get started in the next 5 minutes:\\n1. Complete your profile\\n2. Invite your first team member\\n3. Create your first project\\n\\nWe will be sending you helpful tips over the next two weeks to make sure you get the most out of your account.\\n\\nQuestions? Reply to this email anytime.\\n\\nBest,\\nThe Onboarding Team",
      ),

      // 4. Day-3 tips email
      emailNode(
        "4",
        "Day 3: Getting started tips",
        760,
        "{user_email}",
        "{user_name}, 3 tips to get the most value this week",
        "Hi {user_name},\\n\\nYou have been with us for a few days now. Here are three tips that {user_role}s at companies like {company_name} find most valuable:\\n\\n1. Set up your first automation — most users see results within the first week\\n2. Connect your existing tools — we integrate with 50+ platforms\\n3. Invite your team — collaboration features unlock on {plan_tier} plans\\n\\nEach of these takes less than 5 minutes and will set you up for long-term success.\\n\\nNeed help? Our support team is standing by.\\n\\nBest,\\nThe Onboarding Team",
      ),

      // 5. Day-7 usage check (runCode)
      n("5", "runCode", "Day 7: Check user activation status", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Evaluate user activation status at Day 7
// In production, this would query your application database for usage metrics
const userEmail = vars.user_email || "";
const planTier = vars.plan_tier || "starter";
// Simulate activation check — real implementation pulls from analytics
const activationChecks = {
  profile_completed: true,
  first_project_created: Math.random() > 0.3,
  team_invited: Math.random() > 0.5,
  first_automation_run: Math.random() > 0.6,
  integration_connected: Math.random() > 0.4
};
const completedCount = Object.values(activationChecks).filter(Boolean).length;
const totalChecks = Object.keys(activationChecks).length;
const activationPct = Math.round((completedCount / totalChecks) * 100);
const isActive = activationPct >= 60;
const stalledItems = Object.entries(activationChecks)
  .filter(([_, v]) => !v)
  .map(([k]) => k.replace(/_/g, " "))
  .join(", ");
return {
  activation_percentage: activationPct,
  is_active_user: isActive,
  completed_steps: completedCount,
  total_steps: totalChecks,
  stalled_items: stalledItems || "none",
  user_status: isActive ? "active" : "stalled",
  check_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "activation_percentage", type: "number" },
            { name: "is_active_user", type: "boolean" },
            { name: "completed_steps", type: "number" },
            { name: "total_steps", type: "number" },
            { name: "stalled_items", type: "string" },
            { name: "user_status", type: "string" },
            { name: "check_date", type: "string" },
          ],
        },
      }),

      // 6. Active/stalled condition
      n("6", "condition", "User active (activation >= 60%)?", 1240, {
        conditionData: [
          {
            variable: "is_active_user",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Adaptive check-in email
      emailNode(
        "7",
        "Day 7: Adaptive check-in email",
        1480,
        "{user_email}",
        "{user_name}, how is your first week going?",
        "Hi {user_name},\\n\\nYou have been with us for a week! Here is a quick status on your setup:\\n\\nCompleted: {completed_steps} of {total_steps} activation steps ({activation_percentage}%)\\n\\nStill to do: {stalled_items}\\n\\nI know getting started can feel overwhelming, so here are some resources:\\n- Quick-start video walkthrough (5 min)\\n- Live onboarding session this Thursday at 2 PM\\n- One-on-one setup call with our team\\n\\nReply to this email and we will get you sorted.\\n\\nBest,\\nThe Onboarding Team",
      ),

      // 8. Day-14 value report
      emailNode(
        "8",
        "Day 14: Value report",
        1720,
        "{user_email}",
        "Your first 2 weeks with us, {user_name}",
        "Hi {user_name},\\n\\nYou have been with us for two weeks! Here is your onboarding progress report:\\n\\nPlan: {plan_tier}\\nActivation: {activation_percentage}%\\nStatus: {user_status}\\n\\nWhat is next:\\n- Explore advanced features on your {plan_tier} plan\\n- Join our weekly best-practices webinar\\n- Connect with our community of {user_role}s\\n\\nYour dedicated Customer Success Manager will be reaching out to help you unlock even more value.\\n\\nBest,\\nThe Onboarding Team",
      ),

      // 9. CS handoff (Slack)
      slackNode(
        "9",
        "Slack: CS handoff notification",
        1960,
        ":handshake: Onboarding Sequence Complete — CS Handoff\\nUser: {user_name} ({user_email})\\nCompany: {company_name}\\nPlan: {plan_tier} | Role: {user_role}\\nActivation: {activation_percentage}% ({completed_steps}/{total_steps} steps)\\nStatus: {user_status}\\nStalled items: {stalled_items}\\nPlease schedule a success check-in call.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // active -> positive check-in
      e("6", "7", "output_2"), // stalled -> help-focused check-in
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 592. PHISHING EMAIL REPORTER BOT (8 nodes)
  // ===================================================================

  "Phishing Email Reporter Bot": {
    summary:
      "Employee phishing report handler that analyzes forwarded suspicious emails by extracting headers and URLs, runs threat analysis via runCode, scores threats with AI, branches on threat level, creates security Slack alerts and quarantine actions, files Jira security tickets, acknowledges the reporter, and updates the security awareness record.",
    nodes: [
      trigger("Email Forward: Suspicious email reported", "email", {
        source: "Employee Email Forward",
        event: "phishing.reported",
        howItStarts:
          "An employee forwards a suspicious email to the security reporting address (e.g., phishing@company.com) for analysis",
        setup:
          "Set up a dedicated phishing report email address. Configure it to forward all messages to this workflow webhook with full headers, body, URLs, and attachments metadata preserved.",
        inputFields: [],
      }),

      // 2. Header/URL analysis (runCode)
      n("2", "runCode", "Extract and analyze email indicators", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract phishing indicators from reported email
const payload = JSON.parse(vars.webhook_payload || "{}");
const subject = payload.subject || payload.Subject || "";
const sender = payload.from || payload.From || "";
const body = payload.text || payload.body || payload.TextBody || "";
const headers = payload.headers || payload.Headers || [];
const reporter = payload.forwarded_by || payload.original_recipient || "";
// Extract URLs from body
const urlRegex = /https?:\\/\\/[^\\s<>"{}|\\\\^\\[\\]]+/g;
const urls = (body.match(urlRegex) || []);
const uniqueUrls = [...new Set(urls)];
// Analyze sender domain
const senderDomain = sender.includes("@") ? sender.split("@")[1] : "";
// Check for common phishing indicators
const indicators = [];
if (senderDomain && !senderDomain.match(/\\.(com|org|net|edu|gov)$/)) indicators.push("unusual_tld");
if (subject.toLowerCase().includes("urgent") || subject.toLowerCase().includes("immediately")) indicators.push("urgency_language");
if (body.toLowerCase().includes("password") || body.toLowerCase().includes("verify your account")) indicators.push("credential_request");
if (body.toLowerCase().includes("click here") || body.toLowerCase().includes("click below")) indicators.push("click_bait");
if (uniqueUrls.some(u => u.includes("bit.ly") || u.includes("tinyurl"))) indicators.push("shortened_urls");
if (sender.toLowerCase() !== sender) indicators.push("mixed_case_sender");
const spfPass = headers.some(h => h.Name === "Received-SPF" && h.Value && h.Value.includes("pass"));
if (!spfPass) indicators.push("spf_fail_or_missing");
return {
  reported_subject: subject,
  reported_sender: sender,
  sender_domain: senderDomain,
  reporter_email: reporter,
  extracted_urls: uniqueUrls.join(", ") || "none",
  url_count: uniqueUrls.length,
  phishing_indicators: indicators.join(", ") || "none",
  indicator_count: indicators.length,
  spf_passed: spfPass,
  body_preview: body.substring(0, 500)
};`,
          outputVariables: [
            { name: "reported_subject", type: "string" },
            { name: "reported_sender", type: "string" },
            { name: "sender_domain", type: "string" },
            { name: "reporter_email", type: "string" },
            { name: "extracted_urls", type: "string" },
            { name: "url_count", type: "number" },
            { name: "phishing_indicators", type: "string" },
            { name: "indicator_count", type: "number" },
            { name: "spf_passed", type: "boolean" },
            { name: "body_preview", type: "string" },
          ],
        },
      }),

      // 3. AI threat scoring
      aiNode(
        "3",
        "AI score phishing threat level",
        520,
        "You are a cybersecurity analyst specializing in phishing detection. Analyze the reported email's indicators, sender domain, URLs, and content to determine the threat level. Score from 0-100 (0=safe, 100=confirmed phishing). Consider SPF status, indicator count, URL patterns, and content analysis. Respond JSON: {threat_score: number, threat_level: string, confidence: number, analysis: string, recommended_action: string, is_phishing: boolean, attack_type: string}",
        "Subject: {reported_subject}\\nSender: {reported_sender}\\nDomain: {sender_domain}\\nURLs: {extracted_urls}\\nIndicators: {phishing_indicators}\\nIndicator count: {indicator_count}\\nSPF passed: {spf_passed}\\nBody preview: {body_preview}",
        [
          { field: "threat_score", as: "threat_score", type: "number" },
          { field: "threat_level", as: "threat_level", type: "string" },
          { field: "confidence", as: "analysis_confidence", type: "number" },
          { field: "analysis", as: "threat_analysis", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "is_phishing", as: "is_phishing", type: "boolean" },
          { field: "attack_type", as: "attack_type", type: "string" },
        ],
      ),

      // 4. Threat condition
      n("4", "condition", "Threat score >= 60 (likely phishing)?", 760, {
        conditionData: [
          {
            variable: "threat_score",
            value: "60",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Security Slack alert + quarantine
      slackNode(
        "5",
        "Slack: Security alert — phishing detected",
        1000,
        ":rotating_light: PHISHING ALERT — Threat Level: {threat_level} (Score: {threat_score})\\nReported by: {reporter_email}\\nSubject: {reported_subject}\\nSender: {reported_sender} (Domain: {sender_domain})\\nAttack type: {attack_type}\\nIndicators: {phishing_indicators}\\nURLs found: {extracted_urls}\\n\\nAnalysis: {threat_analysis}\\nRecommended action: {recommended_action}\\n\\nIMPEDIATE ACTIONS: Block sender domain, quarantine similar messages, notify affected users.",
      ),

      // 6. Security ticket (Jira)
      n("6", "hitApi", "Create Jira security ticket", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{security_project_key}"},"summary":"Phishing Report: {reported_subject}","description":"Threat Level: {threat_level} (Score: {threat_score})\\nReported by: {reporter_email}\\nSender: {reported_sender}\\nDomain: {sender_domain}\\nIndicators: {phishing_indicators}\\nURLs: {extracted_urls}\\nAnalysis: {threat_analysis}\\nRecommended: {recommended_action}","issuetype":{"name":"Security Incident"},"priority":{"name":"High"}}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_ticket_id", type: "string" },
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),

      // 7. Reporter acknowledgment
      emailNode(
        "7",
        "Thank reporter and share results",
        1480,
        "{reporter_email}",
        "Phishing Report Received — Thank You",
        "Thank you for reporting the suspicious email.\\n\\nOur analysis is complete:\\n- Subject: {reported_subject}\\n- Sender: {reported_sender}\\n- Threat Level: {threat_level} (Score: {threat_score}/100)\\n- Analysis: {threat_analysis}\\n\\nWe have taken appropriate action including {recommended_action}.\\nSecurity ticket: {jira_ticket_key}\\n\\nYour vigilance helps keep our organization safe. Keep reporting suspicious emails!\\n\\nSecurity Team",
      ),

      // 8. Awareness record update (Sheets)
      sheetsNode(
        "8",
        "Update security awareness record (Sheets)",
        1720,
        "PhishingReports",
        '[["{now}","{reporter_email}","{reported_sender}","{reported_subject}","{threat_level}","{threat_score}","{is_phishing}","{attack_type}","{jira_ticket_key}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // likely phishing -> full security response
      e("4", "7", "output_2"), // low threat -> just acknowledge reporter
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 593. RE-ENGAGEMENT EMAIL CAMPAIGN (8 nodes)
  // ===================================================================

  "Re-Engagement Email Campaign": {
    summary:
      "Dormant user re-engagement campaign that scans for inactive product users, segments by historical value and usage patterns, generates AI 'what's changed' content, sends win-back emails, delays for engagement checking, sends secondary offers, branches on recovery status, and logs revenue recovery data to Sheets.",
    nodes: [
      trigger("Schedule: Weekly dormant user scan", "schedule", {
        schedule: "0 10 * * 3",
        howItStarts:
          "Runs every Wednesday at 10 AM to identify dormant product users who have not logged in for 30+ days and initiate re-engagement campaigns",
        setup:
          "Configure application database or analytics API access to identify dormant users. Set dormant threshold (default: 30 days no login). Set up re-engagement email templates and tracking.",
        inputFields: [],
      }),

      // 2. Value segmentation
      n("2", "runCode", "Segment dormant users by value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment dormant users by their historical value and usage
const dormantUsers = 180; // placeholder — real flow queries analytics
const highValue = Math.floor(dormantUsers * 0.2); // 20% paying/high-usage
const medValue = Math.floor(dormantUsers * 0.4); // 40% moderate usage
const lowValue = dormantUsers - highValue - medValue;
return {
  total_dormant_users: dormantUsers,
  high_value_users: highValue,
  medium_value_users: medValue,
  low_value_users: lowValue,
  dormant_threshold_days: 30,
  campaign_id: "reeng_users_" + new Date().toISOString().split("T")[0],
  scan_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "total_dormant_users", type: "number" },
            { name: "high_value_users", type: "number" },
            { name: "medium_value_users", type: "number" },
            { name: "low_value_users", type: "number" },
            { name: "dormant_threshold_days", type: "number" },
            { name: "campaign_id", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 3. AI "what's changed" content
      aiNode(
        "3",
        "AI generate what's-changed content",
        520,
        "You are a product marketing writer. Generate compelling 'what's changed' re-engagement email content for dormant users. Highlight new features, improvements, and reasons to come back. Create separate messaging for high-value (personal, VIP tone), medium-value (feature-focused), and low-value (simple reminder) segments. Respond JSON: {hv_subject: string, hv_body: string, mv_subject: string, mv_body: string, lv_subject: string, lv_body: string, whats_new_summary: string}",
        "Dormant users: {total_dormant_users}. High value: {high_value_users}. Medium: {medium_value_users}. Low: {low_value_users}. Days inactive: {dormant_threshold_days}+.",
        [
          { field: "hv_subject", as: "hv_reeng_subject", type: "string" },
          { field: "hv_body", as: "hv_reeng_body", type: "string" },
          { field: "mv_subject", as: "mv_reeng_subject", type: "string" },
          { field: "mv_body", as: "mv_reeng_body", type: "string" },
          { field: "lv_subject", as: "lv_reeng_subject", type: "string" },
          { field: "lv_body", as: "lv_reeng_body", type: "string" },
          {
            field: "whats_new_summary",
            as: "whats_new_summary",
            type: "string",
          },
        ],
      ),

      // 4. First win-back email
      emailNode(
        "4",
        "Send first re-engagement email",
        760,
        "{dormant_user_batch}",
        "{hv_reeng_subject}",
        "{hv_reeng_body}",
      ),

      // 5. Delay + login/engagement check
      n("5", "delayAction", "Wait 5 days for login/engagement", 1000, {
        delayDetails: { delayType: "fixed", delayValue: 5, delayUnit: "days" },
      }),

      // 6. Second offer email
      emailNode(
        "6",
        "Send second offer email",
        1240,
        "{still_dormant_batch}",
        "We have made some changes you might like",
        "Hi there,\\n\\nWe noticed you have not been around lately and wanted to share what is new:\\n\\n{whats_new_summary}\\n\\nWe have also prepared a special offer to welcome you back. Log in this week and receive an exclusive bonus.\\n\\n[Come Back and Explore]\\n\\nWe would love to have you back.\\n\\nThe Team",
      ),

      // 7. Recovered/suppressed condition
      n("7", "condition", "User re-engaged (logged in)?", 1480, {
        conditionData: [
          {
            variable: "user_logged_in",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "email_clicked",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 8. Revenue recovery logging (Sheets)
      sheetsNode(
        "8",
        "Log revenue recovery data (Sheets)",
        1720,
        "ReEngagementResults",
        '[["{scan_date}","{campaign_id}","{total_dormant_users}","{high_value_users}","{medium_value_users}","{low_value_users}","{whats_new_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // recovered -> log as win-back success
      e("7", "8", "output_2"), // still dormant -> log for further action
    ],
  },

  // ===================================================================
  // 594. SALES EMAIL OPEN TRACKER TO SLACK (7 nodes)
  // ===================================================================

  "Sales Email Open Tracker to Slack": {
    summary:
      "Real-time sales email engagement tracking that fires on open/click events, enriches contact and deal data from HubSpot, evaluates hot signal conditions, sends instant Slack alerts for engaged prospects, logs CRM activity, updates deal stages, and escalates hot leads to a dedicated channel.",
    nodes: [
      trigger("Webhook: Email open or click event", "webhook_app", {
        source: "Email Tracking Service",
        event: "email.engagement",
        howItStarts:
          "A tracked sales email is opened or a link is clicked, triggering real-time engagement notifications to the sales team",
        setup:
          "Configure your email tracking service (SendGrid, Mixmax, HubSpot Sales) to fire webhooks on open and click events. Include recipient email, event type, timestamp, and message context.",
        inputFields: [],
      }),

      // 2. Contact/deal enrichment (HubSpot)
      n("2", "hitApi", "Enrich contact and deal data (HubSpot)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/{contact_email}?idProperty=email&properties=firstname,lastname,jobtitle,company,lifecyclestage,hubspot_owner_id&associations=deals",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.firstname",
              customName: "contact_first_name",
              type: "string",
            },
            {
              name: "properties.lastname",
              customName: "contact_last_name",
              type: "string",
            },
            {
              name: "properties.jobtitle",
              customName: "contact_title",
              type: "string",
            },
            {
              name: "properties.company",
              customName: "contact_company",
              type: "string",
            },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "properties.hubspot_owner_id",
              customName: "owner_id",
              type: "string",
            },
            {
              name: "associations.deals.results[0].id",
              customName: "deal_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. Hot signal condition
      n("3", "condition", "Hot engagement signal (click event)?", 520, {
        conditionData: [
          {
            variable: "event_type",
            value: "click",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "open_count",
            value: "3",
            comparison_operator: "GREATER THAN OR EQUAL",
            logical_operator: "OR",
          },
        ],
      }),

      // 4. Real-time Slack alert
      slackNode(
        "4",
        "Slack: Real-time engagement alert",
        760,
        ":eyes: Sales Email Engagement Alert\\nContact: {contact_first_name} {contact_last_name} ({contact_email})\\nCompany: {contact_company} | Title: {contact_title}\\nEvent: {event_type} at {event_timestamp}\\nLifecycle: {lifecycle_stage}\\nDeal: {deal_id}\\n\\nThis prospect is showing buying signals — follow up now!",
      ),

      // 5. CRM activity logging
      n("5", "hitApi", "Log engagement activity in HubSpot", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubspot.com/crm/v3/objects/notes",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_note_body":"Email engagement detected: {event_type} at {event_timestamp}. Contact showed hot signal. Follow-up recommended.","hs_timestamp":"{event_timestamp}"}',
            },
            {
              key: "associations",
              value:
                '[{"to":{"id":"{hubspot_contact_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":202}]}]',
            },
          ],
          variables: [{ name: "id", customName: "note_id", type: "string" }],
        },
      }),

      // 6. Deal stage update
      n("6", "hitApi", "Update deal stage in HubSpot", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubspot.com/crm/v3/objects/deals/{deal_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"last_engagement_event":"{event_type}","last_engagement_date":"{event_timestamp}","engagement_signal":"hot"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_deal_id", type: "string" },
            {
              name: "properties.dealstage",
              customName: "current_deal_stage",
              type: "string",
            },
          ],
        },
      }),

      // 7. Hot-lead channel escalation
      slackNode(
        "7",
        "Slack: Hot-lead channel escalation",
        1480,
        ":fire: HOT LEAD ESCALATION\\n{contact_first_name} {contact_last_name} at {contact_company}\\nTitle: {contact_title}\\nEngagement: {event_type} (hot signal confirmed)\\nDeal stage: {current_deal_stage}\\nOwner: {owner_id}\\n\\nPrioritize immediate outreach to this contact.",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // hot signal -> full alert
      e("3", "5", "output_2"), // standard -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 595. SALES FOLLOW UP EMAIL DRIP (8 nodes)
  // ===================================================================

  "Sales Follow Up Email Drip": {
    summary:
      "Post-meeting sales follow-up drip sequence. Pulls meeting notes, sends a Day-1 thank-you email, delivers a Day-4 case study, checks engagement to branch on interest level, sends a proposal or rebooking email, follows up with a breakup email, and updates the deal with manager notification via Slack.",
    nodes: [
      trigger("Webhook: Sales meeting completed", "webhook_app", {
        source: "Calendar / CRM",
        event: "meeting.completed",
        howItStarts:
          "A sales meeting is marked as completed in the CRM or calendar system, triggering a structured follow-up drip sequence",
        setup:
          "Configure your CRM (HubSpot, Salesforce) or calendar tool (Calendly, Google Calendar) to fire a webhook when a sales meeting is completed. Include attendee details, meeting notes, and deal context.",
        inputFields: [],
      }),

      // 2. Meeting notes pull
      aiNode(
        "2",
        "AI extract meeting context",
        280,
        "You are a sales assistant. Parse the meeting completion webhook to extract all relevant context: prospect email, name, company, meeting topic, key discussion points, next steps discussed, objections raised, and deal value. Respond JSON: {prospect_email: string, prospect_name: string, company: string, meeting_topic: string, key_points: string, next_steps: string, objections: string, deal_value: string, meeting_date: string, rep_name: string}",
        "Meeting webhook payload: {webhook_payload}",
        [
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "company", as: "prospect_company", type: "string" },
          { field: "meeting_topic", as: "meeting_topic", type: "string" },
          { field: "key_points", as: "meeting_key_points", type: "string" },
          { field: "next_steps", as: "agreed_next_steps", type: "string" },
          { field: "objections", as: "prospect_objections", type: "string" },
          { field: "deal_value", as: "deal_value", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
        ],
      ),

      // 3. Day-1 thank-you email
      emailNode(
        "3",
        "Day 1: Thank-you email",
        520,
        "{prospect_email}",
        "Great meeting today, {prospect_name}!",
        "Hi {prospect_name},\\n\\nThank you for taking the time to meet today to discuss {meeting_topic}.\\n\\nHere is a quick recap of what we covered:\\n{meeting_key_points}\\n\\nNext steps we agreed on:\\n{agreed_next_steps}\\n\\nI will follow up with some relevant resources that address the points you raised. In the meantime, feel free to reach out with any questions.\\n\\nLooking forward to our next conversation!\\n\\nBest,\\n{rep_name}",
      ),

      // 4. Day-4 case study email
      emailNode(
        "4",
        "Day 4: Case study follow-up",
        760,
        "{prospect_email}",
        "{prospect_name}, a relevant success story for {prospect_company}",
        "Hi {prospect_name},\\n\\nFollowing up on our conversation about {meeting_topic}, I wanted to share a case study from a company in a similar situation to {prospect_company}.\\n\\nThey faced similar challenges and achieved significant results within the first quarter.\\n\\nI think the section on addressing {prospect_objections} might be particularly relevant to your situation.\\n\\n[View the Case Study]\\n\\nWould you like to discuss how these results could translate to {prospect_company}?\\n\\nBest,\\n{rep_name}",
      ),

      // 5. Engagement condition
      n("5", "condition", "Prospect engaged (opened/clicked)?", 1000, {
        conditionData: [
          {
            variable: "followup_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "followup_clicked",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Proposal/rebooking email (engaged)
      emailNode(
        "6",
        "Day 7: Proposal or rebooking email",
        1240,
        "{prospect_email}",
        "Next steps for {prospect_company}, {prospect_name}",
        "Hi {prospect_name},\\n\\nBased on our conversation and the interest you have shown, I have put together a tailored proposal for {prospect_company}.\\n\\nIt addresses the key areas we discussed:\\n{meeting_key_points}\\n\\nAnd specifically covers how we handle {prospect_objections}.\\n\\n[View Your Proposal]\\n\\nI would love to walk through it with you. Would any of these times work for a 20-minute review?\\n\\n- [Time Slot 1]\\n- [Time Slot 2]\\n- [Time Slot 3]\\n\\nBest,\\n{rep_name}",
      ),

      // 7. Breakup email
      emailNode(
        "7",
        "Day 14: Breakup email",
        1480,
        "{prospect_email}",
        "Closing the loop on {meeting_topic}",
        "Hi {prospect_name},\\n\\nI wanted to follow up one final time regarding our discussion about {meeting_topic} for {prospect_company}.\\n\\nI understand priorities shift and timing may not be right. I will close out my follow-ups on this, but please know the offer stands whenever you are ready.\\n\\nIf anything changes, do not hesitate to reach out — I am here to help.\\n\\nWishing you and {prospect_company} all the best,\\n{rep_name}",
        500,
      ),

      // 8. Deal update + manager notification (Slack)
      slackNode(
        "8",
        "Slack: Deal update + manager notification",
        1720,
        ":briefcase: Sales Follow-Up Sequence Complete\\nProspect: {prospect_name} ({prospect_email})\\nCompany: {prospect_company}\\nDeal Value: {deal_value}\\nMeeting Topic: {meeting_topic}\\nObjections: {prospect_objections}\\nNext Steps: {agreed_next_steps}\\nRep: {rep_name}\\n\\nFollow-up sequence has completed. Please review deal status and determine next action.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // engaged -> proposal
      e("5", "7", "output_2"), // not engaged -> breakup
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 596. VOICEMAIL DROP AUTOMATION (7 nodes)
  // ===================================================================

  "Voicemail Drop Automation": {
    summary:
      "Automated voicemail drop for unanswered sales calls. Pulls contact and sequence context, selects the appropriate voicemail script via runCode, triggers the voicemail drop, sends a complementary follow-up SMS via Twilio, logs the call activity in HubSpot CRM, and notifies the rep via Slack.",
    nodes: [
      trigger("Webhook: Outbound call unanswered", "webhook_app", {
        source: "Phone System / Dialer",
        event: "call.no_answer",
        howItStarts:
          "An outbound sales call goes unanswered, triggering an automated voicemail drop with coordinated SMS follow-up",
        setup:
          "Configure your phone system or dialer (Aircall, RingCentral, Twilio) to fire a webhook when an outbound call goes to voicemail. Include caller ID, contact info, call duration, and sequence step.",
        inputFields: [],
      }),

      // 2. Contact/sequence context pull
      aiNode(
        "2",
        "AI extract call and contact context",
        280,
        "You are a data extraction assistant. Parse the call webhook to find the contact's phone number, email, name, company, the sequence or campaign they are in, and which step this call represents. Respond JSON: {phone_number: string, contact_email: string, contact_name: string, company: string, sequence_name: string, sequence_step: number, rep_name: string, rep_phone: string, previous_touchpoints: number}",
        "Call webhook payload: {webhook_payload}",
        [
          { field: "phone_number", as: "contact_phone", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company", as: "contact_company", type: "string" },
          { field: "sequence_name", as: "sequence_name", type: "string" },
          { field: "sequence_step", as: "sequence_step", type: "number" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_phone", as: "rep_phone", type: "string" },
          {
            field: "previous_touchpoints",
            as: "previous_touchpoints",
            type: "number",
          },
        ],
      ),

      // 3. Script selection (runCode)
      n("3", "runCode", "Select voicemail script", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Select the appropriate voicemail script based on sequence step and context
const step = parseInt(vars.sequence_step) || 1;
const name = vars.contact_name || "there";
const company = vars.contact_company || "your company";
const rep = vars.rep_name || "your representative";
const repPhone = vars.rep_phone || "";
let script = "";
let smsFollowup = "";
if (step <= 1) {
  script = "Hi " + name + ", this is " + rep + ". I am reaching out because I think we can help " + company + " with some of the challenges teams in your space are facing. I will send you a quick text with my details. Feel free to call or text me back at " + repPhone + ". Talk soon!";
  smsFollowup = "Hi " + name + ", " + rep + " here. Just left you a voicemail. Would love to chat about how we can help " + company + ". Here is my calendar link: {calendar_link}";
} else if (step <= 3) {
  script = "Hi " + name + ", " + rep + " again. Following up on my earlier outreach to " + company + ". I have some ideas I think could really move the needle for your team. Give me a ring at " + repPhone + " when you get a chance. Thanks!";
  smsFollowup = "Hi " + name + ", following up on my voicemail. I know you are busy — reply STOP to opt out or let me know a good time to chat.";
} else {
  script = "Hi " + name + ", " + rep + " one more time. I know timing is everything, so I will keep this brief. If helping " + company + " is something you would like to explore, I am just a call away at " + repPhone + ". If not, no worries at all. Have a great day!";
  smsFollowup = "Hi " + name + ", final voicemail from " + rep + ". If the timing is not right, no problem. The offer stands whenever you are ready.";
}
return {
  voicemail_script: script,
  sms_message: smsFollowup,
  script_variant: step <= 1 ? "intro" : step <= 3 ? "follow_up" : "breakup",
  drop_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "voicemail_script", type: "string" },
            { name: "sms_message", type: "string" },
            { name: "script_variant", type: "string" },
            { name: "drop_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Voicemail drop via API
      n("4", "hitApi", "Drop voicemail via phone system", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Calls.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{contact_phone}" },
            { key: "From", value: "{rep_phone}" },
            {
              key: "Twiml",
              value:
                "<Response><Say voice='alice'>{voicemail_script}</Say></Response>",
            },
            { key: "MachineDetection", value: "DetectMessageEnd" },
          ],
          variables: [
            { name: "sid", customName: "call_sid", type: "string" },
            { name: "status", customName: "call_status", type: "string" },
          ],
        },
      }),

      // 5. Follow-up SMS (Twilio)
      smsNode(
        "5",
        "Send follow-up SMS",
        1000,
        "{contact_phone}",
        "{sms_message}",
      ),

      // 6. CRM call logging (HubSpot)
      n("6", "hitApi", "Log call activity in HubSpot", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubspot.com/crm/v3/objects/calls",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_call_direction":"OUTBOUND","hs_call_disposition":"no_answer","hs_call_status":"COMPLETED","hs_call_body":"Voicemail dropped ({script_variant}). SMS follow-up sent. Sequence: {sequence_name}, Step: {sequence_step}","hs_timestamp":"{drop_timestamp}","hs_call_to_number":"{contact_phone}"}',
            },
          ],
          variables: [
            { name: "id", customName: "call_log_id", type: "string" },
          ],
        },
      }),

      // 7. Rep notification (Slack)
      slackNode(
        "7",
        "Slack: Voicemail drop notification",
        1480,
        ":phone: Voicemail Drop Complete\\nContact: {contact_name} ({contact_company})\\nPhone: {contact_phone}\\nScript: {script_variant} (Step {sequence_step} of {sequence_name})\\nSMS: Sent follow-up\\nCall SID: {call_sid}\\n\\nRep: {rep_name} — voicemail and SMS delivered. Monitor for callback or reply.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 597. WELCOME EMAIL SERIES (8 nodes)
  // ===================================================================

  "Welcome Email Series": {
    summary:
      "Multi-stage welcome email series for new enrollees. Segments by acquisition source via runCode, sends a personalized welcome email, follows up with a getting-started guide, checks engagement to branch on activity, sends social proof content, delivers an exclusive offer, and transitions to long-term segment management.",
    nodes: [
      trigger("Webhook: New enrollment", "webhook_app", {
        source: "Application / Marketing Platform",
        event: "user.enrolled",
        howItStarts:
          "A new user or subscriber is enrolled in the welcome series from any acquisition source (signup, import, referral, event)",
        setup:
          "Configure your application or marketing platform to fire a webhook when a new contact is enrolled. Include email, name, acquisition source, and any available profile data.",
        inputFields: [],
      }),

      // 2. Source segmentation (runCode)
      n("2", "runCode", "Segment by acquisition source", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse enrollment data and segment by acquisition source
const payload = JSON.parse(vars.webhook_payload || "{}");
const email = payload.email || payload.subscriber_email || payload.user_email || "";
const name = payload.name || payload.first_name || payload.firstName || "";
const source = (payload.source || payload.acquisition_source || payload.utm_source || "direct").toLowerCase();
// Source-based content personalization
const sourceConfig = {
  organic: { tone: "curious explorer", welcomeAngle: "you found us", offerType: "content_upgrade" },
  referral: { tone: "trusted friend", welcomeAngle: "someone you trust recommended us", offerType: "referral_bonus" },
  paid: { tone: "informed buyer", welcomeAngle: "glad our message resonated", offerType: "trial_extension" },
  event: { tone: "engaged attendee", welcomeAngle: "great meeting you", offerType: "exclusive_replay" },
  import: { tone: "valued contact", welcomeAngle: "excited to connect", offerType: "welcome_discount" },
  direct: { tone: "self-starter", welcomeAngle: "welcome aboard", offerType: "getting_started_guide" }
};
const config = sourceConfig[source] || sourceConfig.direct;
return {
  enrollee_email: email,
  enrollee_name: name || "there",
  acquisition_source: source,
  content_tone: config.tone,
  welcome_angle: config.welcomeAngle,
  offer_type: config.offerType,
  enrollment_date: new Date().toISOString(),
  series_id: "welcome_" + Date.now()
};`,
          outputVariables: [
            { name: "enrollee_email", type: "string" },
            { name: "enrollee_name", type: "string" },
            { name: "acquisition_source", type: "string" },
            { name: "content_tone", type: "string" },
            { name: "welcome_angle", type: "string" },
            { name: "offer_type", type: "string" },
            { name: "enrollment_date", type: "string" },
            { name: "series_id", type: "string" },
          ],
        },
      }),

      // 3. Welcome email
      emailNode(
        "3",
        "Send welcome email",
        520,
        "{enrollee_email}",
        "Welcome, {enrollee_name} — {welcome_angle}!",
        "Hi {enrollee_name},\\n\\nWelcome! We are thrilled to have you here.\\n\\nSince {welcome_angle}, we want to make sure you get the most out of your experience with us.\\n\\nHere is what is coming your way over the next few days:\\n1. A quick-start guide to help you hit the ground running\\n2. Our most popular content handpicked for you\\n3. An exclusive offer as a welcome gift\\n\\nFor now, take a look around and get comfortable. We are here if you need anything!\\n\\nWarmly,\\nThe Team",
      ),

      // 4. Getting-started email
      emailNode(
        "4",
        "Send getting-started guide",
        760,
        "{enrollee_email}",
        "{enrollee_name}, here is your quick-start guide",
        "Hi {enrollee_name},\\n\\nReady to get started? Here is your personalized quick-start guide.\\n\\nBased on how you found us ({acquisition_source}), here are the top 3 things to do first:\\n\\n1. Complete your profile — unlock personalized recommendations\\n2. Explore our most popular features — see what others love\\n3. Connect your tools — bring everything together in one place\\n\\nEach step takes less than 2 minutes and will set you up for success.\\n\\n[Start Your Journey]\\n\\nQuestions? Just reply to this email.\\n\\nThe Team",
      ),

      // 5. Engagement check condition
      n("5", "condition", "Enrolled subscriber engaged?", 1000, {
        conditionData: [
          {
            variable: "welcome_email_opened",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Social proof email (engaged)
      emailNode(
        "6",
        "Send social proof email",
        1240,
        "{enrollee_email}",
        "See why thousands love us, {enrollee_name}",
        "Hi {enrollee_name},\\n\\nYou are in great company! Here is what others are saying:\\n\\n'This completely transformed how our team works.' — Sarah K., Marketing Director\\n\\n'I wish I had found this sooner.' — Mike T., Startup Founder\\n\\n'The ROI was clear within the first month.' — Lisa R., VP Operations\\n\\nReady to experience the same results?\\n\\n[Explore More]\\n\\nThe Team",
      ),

      // 7. Exclusive offer email
      emailNode(
        "7",
        "Send exclusive offer email",
        1480,
        "{enrollee_email}",
        "Your exclusive {offer_type} is here, {enrollee_name}",
        "Hi {enrollee_name},\\n\\nAs promised, here is your exclusive welcome offer.\\n\\nBecause {welcome_angle}, we have prepared a special {offer_type} just for you.\\n\\nThis is only available to new members and expires in 7 days.\\n\\n[Claim Your Offer]\\n\\nDo not miss out — this is our way of saying thanks for joining!\\n\\nThe Team",
        500,
      ),

      // 8. Long-term segment transition
      n("8", "hitApi", "Transition to long-term segment (HubSpot)", 1720, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubspot.com/crm/v3/objects/contacts/{enrollee_email}?idProperty=email",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"welcome_series_completed":"true","welcome_series_id":"{series_id}","acquisition_source":"{acquisition_source}","content_tone_preference":"{content_tone}","welcome_offer_type":"{offer_type}","lifecycle_segment":"active_subscriber"}',
            },
          ],
          variables: [{ name: "id", customName: "hubspot_id", type: "string" }],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // engaged -> social proof
      e("5", "7", "output_2"), // not engaged -> go straight to offer
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
